import {
  ArrowRightLeft,
  Building2,
  CalendarDays,
  ExternalLink,
  FolderKanban,
  GitBranch,
  LayoutDashboard,
  LoaderCircle,
  Megaphone,
  Plus,
  Save,
  Search,
  Settings,
  ShieldCheck,
  Trash2,
  UserMinus,
  UserPlus,
  UsersRound,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { createChildOrganization } from "lib/api/createChildOrganization";
import { createOrganization } from "lib/api/createOrganization";
import { createOrganizationInvitation } from "lib/api/createOrganizationInvitation";
import { deleteOrganizationAffiliation } from "lib/api/deleteOrganizationAffiliation";
import { getReceivedOrganizationInvitations } from "lib/api/getReceivedOrganizationInvitations";
import { getOrganizationChart } from "lib/api/getOrganizationChart";
import { getOrganizationSummary } from "lib/api/getOrganizationSummary";
import { moveOrganizationUser } from "lib/api/moveOrganizationUser";
import { removeOrganizationUser } from "lib/api/removeOrganizationUser";
import { respondOrganizationInvitation } from "lib/api/respondOrganizationInvitation";
import { updateOrganization } from "lib/api/updateOrganization";
import { updateOrganizationUserPermission } from "lib/api/updateOrganizationUserPermission";
import { requestGlobalNotificationsRefresh } from "lib/notifications/globalNotifications";
import { useAppStore } from "lib/store/useAppStore";
import {
  formatCountryLabel,
  getCountryDisplayName,
} from "lib/utils/countryLabels";
import {
  formatKoreanDateTime,
  parseUtcDateTime,
} from "lib/utils/koreanDateTime";
import { PERM } from "lib/utils/permission/permissions";
import OrganizationCalendar from "./OrganizationCalendar";
import OrganizationNotices, {
  OrganizationLatestNotice,
} from "./OrganizationNotices";
import OrganizationSummaryWeekCalendar from "./OrganizationSummaryWeekCalendar";

const ROOT_ORG_ID = "current-organization";
const ORGANIZATION_PROJECT_SORT_STORAGE_KEY = "organizationPage.projectSort";

const ORGANIZATION_PROJECT_SORT_OPTIONS = [
  { value: "created_desc", label: "생성일 최신순" },
  { value: "created_asc", label: "생성일 오래된순" },
  { value: "updated_desc", label: "최근 변경 최신순" },
  { value: "updated_asc", label: "최근 변경 오래된순" },
  { value: "name_asc", label: "프로젝트명 오름차순" },
  { value: "name_desc", label: "프로젝트명 내림차순" },
];

const DEFAULT_ORGANIZATION_PROJECT_SORT = ORGANIZATION_PROJECT_SORT_OPTIONS[0].value;

const ORGANIZATION_PERMISSIONS = [
  ["organization.read_all", "전체 조직 조회"],
  ["organization.read_own", "내 조직 조회"],
  ["organization.read_children", "전체 소속 조회"],
  ["organization.child.create", "소속 생성"],
  ["organization.update", "조직 수정"],
  [PERM.ORGANIZATION_DELETE, "하위 소속 삭제"],
  [PERM.ORGANIZATION_NOTICE_WRITE, "조직 공지사항 작성"],
  ["organization.user.read_all", "전체 사용자 조회"],
  ["organization.user.read_own", "내 조직 사용자 조회"],
  ["organization.user.read_children", "전체 소속 사용자 조회"],
  ["organization.user.create", "구성원 추가"],
  [PERM.ORGANIZATION_USER_MOVE, "구성원 소속 조직 이동"],
  ["organization.user.role_update", "사용자 권한 설정"],
  ["organization.user.delete", "사용자 삭제"],
];

const ADMIN_PERMISSION_KEYS = new Set([
  "organization.read_children",
  "organization.child.create",
  "organization.update",
  PERM.ORGANIZATION_DELETE,
  PERM.ORGANIZATION_NOTICE_WRITE,
  "organization.user.create",
  PERM.ORGANIZATION_USER_MOVE,
  "organization.user.role_update",
  "organization.user.delete",
]);

const HIDDEN_PERMISSION_SETTING_KEYS = new Set([
  "organization.read_all",
  "organization.read_children",
  "organization.user.read_all",
  "organization.user.read_own",
  "organization.user.read_children",
  "organization.user.delete",
]);

const TABS = [
  { id: "summary", label: "요약", icon: LayoutDashboard },
  { id: "notices", label: "공지사항", icon: Megaphone },
  { id: "calendar", label: "일정", icon: CalendarDays },
  { id: "projects", label: "프로젝트", icon: FolderKanban },
  { id: "chart", label: "조직도", icon: GitBranch },
  { id: "settings", label: "조직 설정", icon: Settings },
];

const SETTINGS_SECTIONS = [
  { id: "members", label: "구성원 관리", icon: UsersRound },
  { id: "permissions", label: "권한 설정", icon: ShieldCheck },
  { id: "profile", label: "기본 정보", icon: Settings },
];

const ORGANIZATION_ROLE_OPTIONS = [
  { value: "user", label: "사용자" },
  { value: "admin", label: "관리자" },
  { value: "master", label: "최고 관리자" },
];

const INITIAL_CREATE_USER_FORM = {
  email: "",
};

const INITIAL_CREATE_ORGANIZATION_FORM = {
  parent_id: "",
  name: "",
  country_code: "",
  description: "",
};

const INITIAL_ROOT_ORGANIZATION_FORM = {
  name: "",
  country_code: "",
  description: "",
};

const INITIAL_ORGANIZATION_SETTINGS_FORM = {
  name: "",
  description: "",
};

const FALLBACK_COUNTRIES = [
  { code: "KR", default_name: "대한민국" },
  { code: "US", default_name: "미국" },
  { code: "VN", default_name: "베트남" },
  { code: "JP", default_name: "일본" },
  { code: "CN", default_name: "중국" },
];

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function normalizeComparableId(value) {
  if (value == null) return "";
  return String(value).trim().toLowerCase();
}

function formatDateTime(value) {
  return formatKoreanDateTime(value, { sourceTimeZone: "kst" });
}

function toDateTimeNumber(value) {
  const date = parseUtcDateTime(value);
  return date ? date.getTime() : null;
}

function compareNullableDate(leftValue, rightValue, direction = "desc") {
  const left = toDateTimeNumber(leftValue);
  const right = toDateTimeNumber(rightValue);

  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;

  return direction === "asc" ? left - right : right - left;
}

function getInitialOrganizationProjectSort() {
  if (typeof window === "undefined") return DEFAULT_ORGANIZATION_PROJECT_SORT;

  try {
    const savedSort = window.localStorage.getItem(
      ORGANIZATION_PROJECT_SORT_STORAGE_KEY,
    );
    return ORGANIZATION_PROJECT_SORT_OPTIONS.some(
      (option) => option.value === savedSort,
    )
      ? savedSort
      : DEFAULT_ORGANIZATION_PROJECT_SORT;
  } catch {
    return DEFAULT_ORGANIZATION_PROJECT_SORT;
  }
}

function sortOrganizationProjects(projects, sortValue) {
  const rows = Array.isArray(projects) ? [...projects] : [];

  rows.sort((left, right) => {
    if (sortValue === "created_asc") {
      return (
        compareNullableDate(left.createdAt, right.createdAt, "asc") ||
        String(left.name ?? "").localeCompare(String(right.name ?? ""), "ko-KR")
      );
    }

    if (sortValue === "updated_desc" || sortValue === "updated_asc") {
      const direction = sortValue === "updated_asc" ? "asc" : "desc";
      return (
        compareNullableDate(
          left.latestChangeAt,
          right.latestChangeAt,
          direction,
        ) ||
        compareNullableDate(left.createdAt, right.createdAt, direction) ||
        String(left.name ?? "").localeCompare(String(right.name ?? ""), "ko-KR")
      );
    }

    if (sortValue === "name_asc" || sortValue === "name_desc") {
      const compared = String(left.name ?? "").localeCompare(
        String(right.name ?? ""),
        "ko-KR",
      );
      return sortValue === "name_desc" ? -compared : compared;
    }

    return (
      compareNullableDate(left.createdAt, right.createdAt, "desc") ||
      String(left.name ?? "").localeCompare(String(right.name ?? ""), "ko-KR")
    );
  });

  return rows;
}

function parsePermissionValue(value) {
  const normalizePermissionFlags = (permission) =>
    Object.fromEntries(
      Object.entries(permission)
        .filter(([key]) => {
          const text = String(key ?? "").trim();
          return text && text !== "undefined" && text !== "null";
        })
        .map(([key, flag]) => [
          key,
          flag === true ||
            flag === 1 ||
            String(flag).trim().toLowerCase() === "true" ||
            String(flag).trim() === "1",
        ]),
    );

  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) {
    return normalizePermissionFlags(value);
  }

  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? normalizePermissionFlags(parsed)
      : {};
  } catch {
    return {};
  }
}

function normalizeRoleValue(value) {
  const text = String(value ?? "user")
    .trim()
    .toLowerCase();
  return ["master", "admin", "user"].includes(text) ? text : "user";
}

function isMasterActor(organizationRole, appRole) {
  return (
    normalizeRoleValue(appRole) === "master" ||
    normalizeRoleValue(organizationRole) === "master"
  );
}

function getRoleLabel(role) {
  const normalizedRole = normalizeRoleValue(role);
  if (normalizedRole === "master") return "최고 관리자";
  if (normalizedRole === "admin") return "관리자";
  return "사용자";
}

function countAllowedPermissions(permission) {
  return Object.values(parsePermissionValue(permission)).filter(Boolean).length;
}

function canUse(permission = {}, organizationRole, appRole, key) {
  return (
    isMasterActor(organizationRole, appRole) ||
    parsePermissionValue(permission)?.[key] === true
  );
}

function canManageOrganizationUser(permission = {}, organizationRole, appRole) {
  if (isMasterActor(organizationRole, appRole)) return true;
  if (normalizeRoleValue(organizationRole) !== "admin") return false;

  return permission["organization.user.role_update"] === true;
}

function canRequestOrganizationUser(
  permission = {},
  organizationRole,
  appRole,
) {
  if (isMasterActor(organizationRole, appRole)) return true;
  if (normalizeRoleValue(organizationRole) !== "admin") return false;

  return (
    permission["organization.user.create"] === true
  );
}

function canRemoveOrganizationUser(permission = {}, organizationRole, appRole) {
  if (isMasterActor(organizationRole, appRole)) return true;
  if (normalizeRoleValue(organizationRole) !== "admin") return false;

  return permission["organization.user.delete"] === true;
}

function canMoveOrganizationUser(permission = {}, organizationRole, appRole) {
  if (isMasterActor(organizationRole, appRole)) return true;
  if (normalizeRoleValue(organizationRole) !== "admin") return false;

  return permission[PERM.ORGANIZATION_USER_MOVE] === true;
}

function canCreateOrganization(permission = {}, organizationRole, appRole) {
  if (normalizeRoleValue(organizationRole) === "master") return true;
  if (normalizeRoleValue(organizationRole) !== "admin") return false;

  return (
    permission["organization.child.create"] === true
  );
}

function canUpdateOrganization(permission = {}, organizationRole, appRole) {
  if (isMasterActor(organizationRole, appRole)) return true;
  if (normalizeRoleValue(organizationRole) !== "admin") return false;

  return permission["organization.update"] === true;
}

function canDeleteOrganizationAffiliation(
  permission = {},
  organizationRole,
  appRole,
) {
  if (isMasterActor(organizationRole, appRole)) return true;
  if (normalizeRoleValue(organizationRole) !== "admin") return false;

  return permission[PERM.ORGANIZATION_DELETE] === true;
}

function canCreateRootOrganization(permission = {}, appRole) {
  return (
    normalizeRoleValue(appRole) === "master" ||
    parsePermissionValue(permission)?.["organization.create"] === true
  );
}

function canReadChildOrganizations(permission = {}, organizationRole, appRole) {
  if (isMasterActor(organizationRole, appRole)) return true;
  return normalizeRoleValue(organizationRole) === "admin";
}

function getAccessScope(permission = {}, organizationRole, appRole) {
  if (isMasterActor(organizationRole, appRole)) {
    return "전체 조직";
  }
  if (normalizeRoleValue(organizationRole) === "admin") {
    return "최상위 조직의 전체 소속";
  }
  if (permission["organization.read_own"]) return "내 조직";
  return "제한됨";
}

function arePermissionDraftsEqual(left = {}, right = {}) {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);

  for (const key of keys) {
    if (Boolean(left[key]) !== Boolean(right[key])) return false;
  }

  return true;
}

function buildPermissionDraftForOrganizationRole(
  organizationRole,
  currentPermission = {},
) {
  const normalizedRole = normalizeRoleValue(organizationRole);
  const nextPermission = { ...parsePermissionValue(currentPermission) };

  for (const [key] of ORGANIZATION_PERMISSIONS) {
    nextPermission[key] = false;
  }

  if (normalizedRole === "master") {
    for (const [key] of ORGANIZATION_PERMISSIONS) {
      nextPermission[key] = true;
    }
    return nextPermission;
  }

  nextPermission["organization.read_own"] = true;
  nextPermission["organization.user.read_own"] = true;

  if (normalizedRole === "admin") {
    for (const key of ADMIN_PERMISSION_KEYS) {
      nextPermission[key] = true;
    }
    nextPermission["organization.user.read_children"] = true;
  }

  return nextPermission;
}

function getCurrentOrganization({
  user,
  permission,
  summaryOrganization,
  projectCount,
  childCount,
  memberCount,
}) {
  const name =
    summaryOrganization?.name ||
    user?.organization_name ||
    user?.organizationName ||
    user?.organization_label ||
    (user?.organization_id ? "조직명 미지정" : "개인 작업 영역");

  return {
    id: ROOT_ORG_ID,
    organization_id: summaryOrganization?.id ?? user?.organization_id ?? null,
    name,
    code: user?.organization_code ?? "-",
    parentName: "-",
    role: summaryOrganization?.role ?? user?.organization_role ?? null,
    scope: getAccessScope(permission, user?.organization_role, user?.role),
    memberCount,
    projectCount,
    childCount,
    status: user?.organization_id ? "active" : "unassigned",
    source: "current",
  };
}

function getProjectRows(projectTree, user) {
  const treeRows = Array.isArray(projectTree)
    ? projectTree
        .map((project, index) => ({
          id:
            project?.project_id ??
            project?.id ??
            user?.last_selected_project ??
            `project-${index}`,
          name:
            project?.project_name ??
            project?.name ??
            user?.last_selected_project_name ??
            "-",
          owner: user?.organization_name || "현재 소속 조직",
          createdBy: user?.user_name ?? "-",
          status: project?.status ?? "연결됨",
          createdAt: project?.created_at ?? null,
          latestChangeAt:
            project?.latestChangeAt ?? project?.created_at ?? null,
        }))
        .filter((project) => project.id && project.name !== "-")
    : [];

  if (treeRows.length > 0 || !user?.last_selected_project) return treeRows;

  return [
    {
      id: user.last_selected_project,
      name: user.last_selected_project_name || user.last_selected_project,
      owner: user?.organization_name || "현재 소속 조직",
      createdBy: user?.user_name ?? "-",
      status: "최근 선택",
      createdAt: null,
      latestChangeAt: null,
    },
  ];
}

function normalizeSummaryProjects(summaryProjects, fallbackProjects) {
  if (Array.isArray(summaryProjects)) {
    return summaryProjects.map((project) => ({
      id: project.id,
      organizationId: project.organizationId ?? project.organization_id ?? null,
      name: project.name ?? project.project_name ?? "-",
      owner: project.ownerName ?? project.owner ?? "-",
      createdBy:
        project.createdByName ??
        project.createdBy ??
        project.ownerName ??
        project.owner ??
        "-",
      status: project.latestChangeAt ? "최근 변경 있음" : "등록됨",
      contractName: project.contractName ?? "",
      contractor: project.contractor ?? "",
      purpose: project.purpose ?? "",
      createdAt: project.createdAt ?? null,
      latestChangeAt: project.latestChangeAt ?? project.createdAt ?? null,
    }));
  }

  return fallbackProjects;
}

function normalizeMemberActivities(activities) {
  if (!Array.isArray(activities)) return [];

  return activities.map((activity) => ({
    id:
      activity.id ??
      `${activity.title ?? activity.api_title ?? "activity"}-${activity.createdAt ?? activity.created_at ?? ""}`,
    title: activity.title ?? activity.api_title ?? "활동",
    type: activity.type ?? activity.api_type ?? "",
    message: activity.message ?? activity.log_message ?? "",
    projectName: activity.projectName ?? activity.project_name ?? "",
    createdAt: activity.createdAt ?? activity.created_at ?? null,
    isSuccess: activity.isSuccess ?? activity.is_success ?? null,
  }));
}

function normalizeSummaryMembers(summaryMembers, user, allowedPermissionCount) {
  if (Array.isArray(summaryMembers) && summaryMembers.length > 0) {
    return summaryMembers.map((member) => {
      const permission = parsePermissionValue(member.permission);
      const appRoleValue = normalizeRoleValue(member.appRole ?? member.role);
      const organizationRoleValue = normalizeRoleValue(
        member.organizationRole ?? member.organization_role,
      );

      return {
        id: member.id ?? member.email ?? "-",
        organizationId:
          member.affiliationId ??
          member.organizationId ??
          member.organization_id ??
          null,
        organizationName:
          member.organizationName ?? member.organization_name ?? "",
        name: member.name ?? member.user_name ?? "-",
        email: member.email ?? "-",
        appRoleValue,
        appRole: getRoleLabel(appRoleValue),
        organizationRoleValue,
        organizationRole: getRoleLabel(organizationRoleValue),
        permission,
        permissionCount:
          member.permissionCount == null
            ? countAllowedPermissions(permission)
            : toNumber(member.permissionCount),
        projectCount: toNumber(member.projectCount),
        createdAt: member.createdAt ?? member.created_at ?? null,
        lastLoginAt: member.lastLoginAt ?? member.last_login ?? null,
        latestActivityAt:
          member.latestActivityAt ??
          member.lastActivityAt ??
          member.last_login ??
          null,
        recentActivities: normalizeMemberActivities(member.recentActivities),
      };
    });
  }

  if (!user) return [];

  const permission = parsePermissionValue(user?.permission);
  const appRoleValue = normalizeRoleValue(user?.role);
  const organizationRoleValue = normalizeRoleValue(user?.organization_role);

  return [
    {
      id: user?.id ?? "-",
      organizationId: user?.organization_id ?? null,
      organizationName:
        user?.organization_name ?? user?.organizationName ?? "",
      name: user?.user_name ?? "-",
      email: user?.email ?? "-",
      appRoleValue,
      appRole: getRoleLabel(appRoleValue),
      organizationRoleValue,
      organizationRole: getRoleLabel(organizationRoleValue),
      permission,
      permissionCount:
        countAllowedPermissions(permission) || toNumber(allowedPermissionCount),
      projectCount: 0,
      createdAt: null,
      lastLoginAt: user?.lastLogin ?? user?.last_login ?? null,
      latestActivityAt: user?.lastLogin ?? user?.last_login ?? null,
      recentActivities: [],
    },
  ];
}

function normalizeOrganizationActiveStatus(organization = {}) {
  const rawStatus = String(organization.status ?? "")
    .trim()
    .toLowerCase();
  const rawActive = organization.isActive ?? organization.is_active;
  const activeText = String(rawActive ?? "")
    .trim()
    .toLowerCase();

  if (
    rawStatus === "inactive" ||
    rawStatus === "비활성" ||
    rawStatus === "비활성화" ||
    rawActive === false ||
    rawActive === 0 ||
    activeText === "false" ||
    activeText === "0" ||
    activeText === "inactive"
  ) {
    return "inactive";
  }

  if (
    rawStatus === "active" ||
    rawStatus === "활성" ||
    rawStatus === "활성화" ||
    rawActive === true ||
    rawActive === 1 ||
    activeText === "true" ||
    activeText === "1" ||
    activeText === "active"
  ) {
    return "active";
  }

  return rawStatus || "active";
}

function normalizeChildOrganizations(children, parentName) {
  if (!Array.isArray(children)) return [];

  const normalized = children.map((organization, index) => {
    const status = normalizeOrganizationActiveStatus(organization);
    const parentId = organization.parentId ?? organization.parent_id ?? null;
    const depth = Math.max(
      0,
      toNumber(organization.depth, parentId ? 1 : 0),
    );
    const countryCode =
      organization.countryCode ?? organization.country_code ?? organization.code ?? null;
    const countryName = getCountryDisplayName({
      code: countryCode,
      country_name: organization.countryName ?? organization.country_name,
      default_name:
        organization.defaultCountryName ?? organization.default_country_name,
    });

    return {
      id: organization.id ?? organization.organization_id ?? `child-${index}`,
      organization_id: organization.organization_id ?? organization.id ?? null,
      parentId,
      name: organization.name ?? organization.organization_name ?? "-",
      code: countryCode ?? "-",
      countryName: countryName || "-",
      parentName: organization.parentName ?? (parentId ? parentName : "-"),
      role: organization.role ?? null,
      countryCode,
      isActive: status !== "inactive",
      scope: depth === 0 ? "최상위 조직" : "소속",
      memberCount: toNumber(organization.memberCount),
      projectCount: toNumber(organization.projectCount),
      childCount: toNumber(organization.childCount),
      status,
      depth,
      source: "child",
    };
  });

  const nameById = new Map(
    normalized.map((organization) => [
      organization.organization_id,
      organization.name,
    ]),
  );

  return normalized.map((organization) => ({
    ...organization,
    parentName:
      nameById.get(organization.parentId) ||
      organization.parentName ||
      (organization.parentId ? parentName : "-"),
  }));
}

function normalizeOrganizationPath(path) {
  if (!Array.isArray(path)) return [];

  return path
    .map((organization, index) => {
      const organizationId =
        organization?.organization_id ?? organization?.id ?? null;
      const normalizedId = normalizeComparableId(organizationId);
      if (!normalizedId) return null;

      return {
        id: normalizedId,
        organization_id: organizationId,
        parentId: organization?.parentId ?? organization?.parent_id ?? null,
        name:
          organization?.name ??
          organization?.organization_name ??
          `Organization ${index + 1}`,
      };
    })
    .filter(Boolean);
}

function getOrganizationActualId(organization) {
  return normalizeComparableId(
    organization?.organization_id || organization?.id,
  );
}

function sortOrganizationTreeRows(rows, rootOrganizationId = null) {
  const rowById = new Map();

  rows.forEach((row) => {
    const organizationId = getOrganizationActualId(row);
    if (!organizationId || rowById.has(organizationId)) return;
    rowById.set(organizationId, {
      ...row,
      id: organizationId,
      organization_id: organizationId,
    });
  });

  const childrenByParentId = new Map();
  const roots = [];

  rowById.forEach((row) => {
    const parentId = normalizeComparableId(row.parentId);
    if (parentId && rowById.has(parentId)) {
      const siblings = childrenByParentId.get(parentId) ?? [];
      siblings.push(row);
      childrenByParentId.set(parentId, siblings);
      return;
    }

    roots.push(row);
  });

  const sortByName = (left, right) =>
    String(left.name ?? "").localeCompare(String(right.name ?? ""), "ko");
  roots.sort(sortByName);
  childrenByParentId.forEach((children) => children.sort(sortByName));

  const sortedRows = [];
  const visited = new Set();
  const visit = (row, depth) => {
    const organizationId = getOrganizationActualId(row);
    if (!organizationId || visited.has(organizationId)) return;
    visited.add(organizationId);

    const parentId = normalizeComparableId(row.parentId);
    const parent = parentId ? rowById.get(parentId) : null;
    sortedRows.push({
      ...row,
      depth,
      parentName: parent?.name || row.parentName || "-",
    });

    (childrenByParentId.get(organizationId) ?? []).forEach((child) =>
      visit(child, depth + 1),
    );
  };

  const normalizedRootOrganizationId = normalizeComparableId(
    rootOrganizationId,
  );
  const requestedRoot = normalizedRootOrganizationId
    ? rowById.get(normalizedRootOrganizationId)
    : null;

  if (requestedRoot) {
    visit(requestedRoot, 0);
  } else {
    roots.forEach((root) => visit(root, 0));
    rowById.forEach((row) => {
      const organizationId = getOrganizationActualId(row);
      if (!visited.has(organizationId)) visit(row, toNumber(row.depth, 0));
    });
  }

  return sortedRows;
}

function getMemberActivityTitle(activity) {
  return activity?.title || activity?.message || "활동";
}

function normalizeOrganizationTabId(tabId) {
  if (tabId === "children") return "chart";
  if (tabId === "members" || tabId === "permissions") return "settings";
  return tabId;
}

function normalizeOrganizationChartData(data) {
  const organizations = Array.isArray(data?.organizations)
    ? data.organizations.map((organization, index) => {
        const organizationId =
          organization?.organizationId ??
          organization?.organization_id ??
          organization?.id ??
          `chart-organization-${index}`;
        const countryCode =
          organization?.countryCode ?? organization?.country_code ?? null;

        return {
          ...organization,
          id: organizationId,
          organization_id: organizationId,
          parentId:
            organization?.parentId ?? organization?.parent_id ?? null,
          name:
            organization?.name ?? organization?.organization_name ?? "-",
          countryCode,
          countryName:
            getCountryDisplayName({
              code: countryCode,
              country_name:
                organization?.countryName ?? organization?.country_name,
            }) || "-",
          isActive: organization?.isActive !== false,
          status:
            organization?.status ??
            (organization?.isActive === false ? "inactive" : "active"),
          memberCount: toNumber(organization?.memberCount),
          childCount: toNumber(organization?.childCount),
          depth: toNumber(organization?.depth),
        };
      })
    : [];
  const members = Array.isArray(data?.members)
    ? data.members.map((member, index) => ({
        ...member,
        id: member?.id ?? `chart-member-${index}`,
        organizationId:
          member?.affiliationId ??
          member?.organizationId ??
          member?.organization_id ??
          null,
        name: member?.name ?? member?.user_name ?? "-",
        email: member?.email ?? "-",
        organizationRoleValue: normalizeRoleValue(
          member?.organizationRole ?? member?.organization_role,
        ),
        organizationRole: getRoleLabel(
          member?.organizationRole ?? member?.organization_role,
        ),
      }))
    : [];

  return {
    rootOrganizationId: normalizeComparableId(data?.rootOrganizationId),
    currentOrganizationId: normalizeComparableId(
      data?.currentOrganizationId,
    ),
    organizations,
    members,
  };
}

function buildOrganizationChartForest(organizations, members) {
  const memberRowsByOrganizationId = new Map();
  members.forEach((member) => {
    const organizationId = normalizeComparableId(member.organizationId);
    if (!organizationId) return;
    const rows = memberRowsByOrganizationId.get(organizationId) ?? [];
    rows.push(member);
    memberRowsByOrganizationId.set(organizationId, rows);
  });

  const nodeById = new Map();
  organizations.forEach((organization) => {
    const organizationId = getOrganizationActualId(organization);
    if (!organizationId || nodeById.has(organizationId)) return;
    nodeById.set(organizationId, {
      ...organization,
      id: organizationId,
      organization_id: organizationId,
      members: memberRowsByOrganizationId.get(organizationId) ?? [],
      children: [],
    });
  });

  const roots = [];
  nodeById.forEach((node) => {
    const parentId = normalizeComparableId(node.parentId);
    const parent = parentId ? nodeById.get(parentId) : null;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes) => {
    nodes.sort((left, right) =>
      String(left.name ?? "").localeCompare(String(right.name ?? ""), "ko"),
    );
    nodes.forEach((node) => sortNodes(node.children));
  };
  sortNodes(roots);

  return roots;
}

function OrganizationChartNode({
  canCreateChild,
  canDelete,
  canManageMember,
  currentOrganizationId,
  node,
  onCreateChild,
  onDelete,
  onManageMember,
  onSelectOrganization,
}) {
  const organizationId = getOrganizationActualId(node);
  const isCurrent = organizationId === currentOrganizationId;
  const visibleMembers = node.members;

  return (
    <li className="organizationPage__chartNode">
      <article
        className={`organizationPage__chartCard ${
          isCurrent ? "isCurrent" : ""
        } ${node.status === "inactive" ? "isInactive" : ""}`.trim()}
      >
        <button
          className="organizationPage__chartCardHeader"
          onClick={() => onSelectOrganization(node)}
          type="button"
        >
          <span className="organizationPage__chartCardIcon">
            <Building2 size={18} aria-hidden="true" />
          </span>
          <span>
            <strong>{node.name}</strong>
            <small>
              {node.depth === 0 ? "최상위 조직" : node.countryName || "조직"}
            </small>
          </span>
          {isCurrent && (
            <em className="organizationPage__chartCurrentBadge">내 조직</em>
          )}
        </button>

        <div className="organizationPage__chartCardMeta">
          <span>
            <UsersRound size={13} aria-hidden="true" />
            구성원 {node.members.length}명
          </span>
          <span>내부 소속 {node.children.length}개</span>
        </div>

        <div className="organizationPage__chartMembers">
          {visibleMembers.length > 0 ? (
            visibleMembers.map((member) => {
              const isManageable = canManageMember(member);
              const memberContent = (
                <>
                  <span className="organizationPage__chartMemberAvatar">
                    {String(member.name || "?").slice(0, 1)}
                  </span>
                  <span>
                    <strong>{member.name}</strong>
                    <small>{member.organizationRole}</small>
                  </span>
                  {isManageable && (
                    <ArrowRightLeft size={13} aria-hidden="true" />
                  )}
                </>
              );

              return isManageable ? (
                <button
                  aria-label={`${member.name} 구성원 관리`}
                  key={member.id}
                  onClick={() => onManageMember(member)}
                  type="button"
                >
                  {memberContent}
                </button>
              ) : (
                <div key={member.id}>{memberContent}</div>
              );
            })
          ) : (
            <p>소속 구성원이 없습니다.</p>
          )}
        </div>

        {(canCreateChild(node) || canDelete(node)) && (
          <div className="organizationPage__chartActions">
            {canCreateChild(node) && (
              <button
                className="organizationPage__chartAddButton"
                onClick={() => onCreateChild(node)}
                type="button"
              >
                <Plus size={14} aria-hidden="true" />
                <span>소속 추가</span>
              </button>
            )}
            {canDelete(node) && (
              <button
                aria-label={`${node.name} 소속 삭제`}
                className="organizationPage__chartDeleteButton"
                onClick={() => onDelete(node)}
                type="button"
              >
                <Trash2 size={14} aria-hidden="true" />
                <span>소속 삭제</span>
              </button>
            )}
          </div>
        )}
      </article>

      {node.children.length > 0 && (
        <ul>
          {node.children.map((child) => (
            <OrganizationChartNode
              canCreateChild={canCreateChild}
              canDelete={canDelete}
              canManageMember={canManageMember}
              currentOrganizationId={currentOrganizationId}
              key={child.id}
              node={child}
              onCreateChild={onCreateChild}
              onDelete={onDelete}
              onManageMember={onManageMember}
              onSelectOrganization={onSelectOrganization}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function OrganizationPage() {
  const user = useAppStore((s) => s.user);
  const projectTree = useAppStore((s) => s.projectTree);
  const showAlert = useAppStore((s) => s.showAlert);
  const refreshAuth = useAppStore((s) => s.refresh);
  const loadAuthenticatedUser = useAppStore((s) => s.me);
  const setAuthenticated = useAppStore((s) => s.setAuthenticated);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrganizationQueryId = normalizeComparableId(
    searchParams.get("organization_id"),
  );
  const selectedOrganizationTab = searchParams.get("tab") ?? "";
  const selectedSettingsSection = searchParams.get("section") ?? "";

  const permission = useMemo(
    () => parsePermissionValue(user?.permission),
    [user?.permission],
  );
  const organizationRole = user?.organization_role;
  const appRole = user?.role;
  const isSystemMaster = normalizeRoleValue(appRole) === "master";
  const canAssignMasterRole = isMasterActor(organizationRole, appRole);
  const canViewPermissionSettings =
    canManageOrganizationUser(permission, organizationRole, appRole) ||
    canRemoveOrganizationUser(permission, organizationRole, appRole);
  const canViewChildOrganizations = canReadChildOrganizations(
    permission,
    organizationRole,
    appRole,
  );
  const canUpdateOrganizationSettings = canUpdateOrganization(
    permission,
    organizationRole,
    appRole,
  );
  const canAccessOrganizationSettings = ["admin", "master"].includes(
    normalizeRoleValue(organizationRole),
  );
  const visibleTabs = useMemo(
    () =>
      TABS.filter(
        (tab) => tab.id !== "settings" || canAccessOrganizationSettings,
      ),
    [canAccessOrganizationSettings],
  );
  const visibleSettingsSections = useMemo(
    () =>
      SETTINGS_SECTIONS.filter(
        (section) =>
          (section.id !== "permissions" || canViewPermissionSettings) &&
          (section.id !== "profile" || canUpdateOrganizationSettings),
      ),
    [canUpdateOrganizationSettings, canViewPermissionSettings],
  );

  const [activeTab, setActiveTab] = useState("summary");
  const [settingsSection, setSettingsSection] = useState(() => {
    if (selectedOrganizationTab === "permissions") return "permissions";
    if (selectedSettingsSection) return selectedSettingsSection;
    return "members";
  });
  const [selectedOrgId, setSelectedOrgId] = useState(ROOT_ORG_ID);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryError, setSummaryError] = useState("");
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [rootMemberSummaryData, setRootMemberSummaryData] = useState(null);
  const [rootMemberSummaryError, setRootMemberSummaryError] = useState("");
  const [isLoadingRootMembers, setIsLoadingRootMembers] = useState(false);
  const [organizationChart, setOrganizationChart] = useState(() =>
    normalizeOrganizationChartData(null),
  );
  const [organizationChartError, setOrganizationChartError] = useState("");
  const [isLoadingOrganizationChart, setIsLoadingOrganizationChart] =
    useState(false);
  const [memberQuery, setMemberQuery] = useState("");
  const [projectSort, setProjectSort] = useState(
    getInitialOrganizationProjectSort,
  );
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [draftOrganizationRole, setDraftOrganizationRole] = useState("user");
  const [draftMemberPermission, setDraftMemberPermission] = useState({});
  const [savingMember, setSavingMember] = useState(false);
  const [removingMember, setRemovingMember] = useState(false);
  const [movingMember, setMovingMember] = useState(false);
  const [moveTargetOrganizationId, setMoveTargetOrganizationId] = useState("");
  const [isRemoveMemberConfirmOpen, setIsRemoveMemberConfirmOpen] =
    useState(false);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isSubmittingCreateUser, setIsSubmittingCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState(
    INITIAL_CREATE_USER_FORM,
  );
  const [isCreateOrganizationOpen, setIsCreateOrganizationOpen] =
    useState(false);
  const [isSubmittingCreateOrganization, setIsSubmittingCreateOrganization] =
    useState(false);
  const [createOrganizationForm, setCreateOrganizationForm] = useState(
    INITIAL_CREATE_ORGANIZATION_FORM,
  );
  const [deleteOrganizationTarget, setDeleteOrganizationTarget] =
    useState(null);
  const [isDeletingOrganization, setIsDeletingOrganization] = useState(false);
  const [rootOrganizationForm, setRootOrganizationForm] = useState(
    INITIAL_ROOT_ORGANIZATION_FORM,
  );
  const [organizationSettingsForm, setOrganizationSettingsForm] = useState(
    INITIAL_ORGANIZATION_SETTINGS_FORM,
  );
  const [isSavingOrganizationSettings, setIsSavingOrganizationSettings] =
    useState(false);
  const countryOptions = FALLBACK_COUNTRIES;
  const [isSubmittingRootOrganization, setIsSubmittingRootOrganization] =
    useState(false);
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  const [respondingInvitationId, setRespondingInvitationId] = useState("");

  const notify = useCallback(
    (text, success = true) => {
      showAlert?.({
        text,
        success,
        autoClose: success,
      });
    },
    [showAlert],
  );

  const handleOrganizationTabChange = useCallback(
    (tabId) => {
      const normalizedTabId = normalizeOrganizationTabId(tabId);
      if (!visibleTabs.some((tab) => tab.id === normalizedTabId)) return;

      if (tabId === "members" || tabId === "permissions") {
        setSettingsSection(tabId);
      }

      const nextSearchParams = new URLSearchParams(searchParams);
      if (normalizedTabId === "summary") {
        nextSearchParams.delete("tab");
      } else {
        nextSearchParams.set("tab", normalizedTabId);
      }

      if (normalizedTabId === "settings") {
        nextSearchParams.set(
          "section",
          tabId === "members" || tabId === "permissions"
            ? tabId
            : settingsSection,
        );
      } else {
        nextSearchParams.delete("section");
      }

      if (normalizedTabId !== "calendar") {
        nextSearchParams.delete("schedule_id");
        nextSearchParams.delete("schedule_date");
      }

      setActiveTab(normalizedTabId);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams, settingsSection, visibleTabs],
  );

  const handleSettingsSectionChange = useCallback(
    (sectionId) => {
      if (!visibleSettingsSections.some((section) => section.id === sectionId)) {
        return;
      }

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("tab", "settings");
      nextSearchParams.set("section", sectionId);
      setSettingsSection(sectionId);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams, visibleSettingsSections],
  );

  const loadOrganizationSummary = useCallback(async () => {
    setSummaryError("");
    setIsLoadingSummary(true);

    try {
      const result = await getOrganizationSummary({
        organizationId: selectedOrganizationQueryId || null,
      });
      if (result.success) {
        setSummaryData(result.data);
      } else {
        setSummaryError(result.message || "조직 요약을 불러오지 못했습니다.");
      }
    } catch {
      setSummaryError("조직 요약을 불러오지 못했습니다.");
    } finally {
      setIsLoadingSummary(false);
    }
  }, [selectedOrganizationQueryId]);

  const loadOrganizationChart = useCallback(async () => {
    if (!user?.organization_id) {
      setOrganizationChart(normalizeOrganizationChartData(null));
      setOrganizationChartError("");
      return;
    }

    setIsLoadingOrganizationChart(true);
    setOrganizationChartError("");
    const result = await getOrganizationChart();
    setIsLoadingOrganizationChart(false);

    if (!result.success) {
      setOrganizationChartError(
        result.message || "조직도를 불러오지 못했습니다.",
      );
      return;
    }

    setOrganizationChart(normalizeOrganizationChartData(result.data));
  }, [user?.organization_id]);

  const loadRootOrganizationMembers = useCallback(async () => {
    if (!canAccessOrganizationSettings) {
      setRootMemberSummaryData(null);
      setRootMemberSummaryError("");
      setIsLoadingRootMembers(false);
      return;
    }

    const rootOrganizationId = normalizeComparableId(
      summaryData?.organization?.rootOrganizationId ||
        organizationChart.rootOrganizationId ||
        user?.organization_root_id ||
        user?.organizationRootId,
    );
    if (!rootOrganizationId) return;

    const loadedOrganizationId = normalizeComparableId(
      summaryData?.organization?.id,
    );
    if (loadedOrganizationId === rootOrganizationId && summaryData?.members) {
      setRootMemberSummaryData(summaryData);
      setRootMemberSummaryError("");
      setIsLoadingRootMembers(false);
      return;
    }

    setRootMemberSummaryError("");
    setIsLoadingRootMembers(true);
    try {
      const result = await getOrganizationSummary({
        organizationId: rootOrganizationId,
      });
      if (result.success) {
        setRootMemberSummaryData(result.data);
      } else {
        setRootMemberSummaryData(null);
        setRootMemberSummaryError(
          result.message || "전체 구성원 정보를 불러오지 못했습니다.",
        );
      }
    } catch {
      setRootMemberSummaryData(null);
      setRootMemberSummaryError("전체 구성원 정보를 불러오지 못했습니다.");
    } finally {
      setIsLoadingRootMembers(false);
    }
  }, [
    canAccessOrganizationSettings,
    organizationChart.rootOrganizationId,
    summaryData,
    user?.organizationRootId,
    user?.organization_root_id,
  ]);

  const loadReceivedInvitations = useCallback(async () => {
    if (!user?.id) {
      setReceivedInvitations([]);
      setIsLoadingInvitations(false);
      return;
    }

    setIsLoadingInvitations(true);
    const result = await getReceivedOrganizationInvitations();
    setIsLoadingInvitations(false);

    if (!result.success) {
      setReceivedInvitations([]);
      return;
    }

    setReceivedInvitations(
      Array.isArray(result.data?.invitations)
        ? result.data.invitations
        : [],
    );
  }, [user?.id]);

  useEffect(() => {
    loadOrganizationSummary();
  }, [loadOrganizationSummary]);

  useEffect(() => {
    loadReceivedInvitations();
  }, [loadReceivedInvitations]);

  useEffect(() => {
    loadOrganizationChart();
  }, [loadOrganizationChart]);

  useEffect(() => {
    if (activeTab !== "settings") return;
    loadRootOrganizationMembers();
  }, [activeTab, loadRootOrganizationMembers]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab("summary");
    }
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    const normalizedTabId = normalizeOrganizationTabId(selectedOrganizationTab);
    if (
      normalizedTabId &&
      visibleTabs.some((tab) => tab.id === normalizedTabId)
    ) {
      setActiveTab((currentTab) =>
        currentTab === normalizedTabId
          ? currentTab
          : normalizedTabId,
      );
    }
  }, [selectedOrganizationTab, visibleTabs]);

  useEffect(() => {
    const legacySection = ["members", "permissions"].includes(
      selectedOrganizationTab,
    )
      ? selectedOrganizationTab
      : null;
    const requestedSection = legacySection || selectedSettingsSection;
    if (
      requestedSection &&
      visibleSettingsSections.some(
        (section) => section.id === requestedSection,
      )
    ) {
      setSettingsSection(requestedSection);
      return;
    }

    if (!visibleSettingsSections.some((section) => section.id === settingsSection)) {
      setSettingsSection(visibleSettingsSections[0]?.id ?? "members");
    }
  }, [
    selectedOrganizationTab,
    selectedSettingsSection,
    settingsSection,
    visibleSettingsSections,
  ]);

  useEffect(() => {
    if (selectedOrganizationTab) return;
    setActiveTab("summary");
  }, [selectedOrganizationQueryId, selectedOrganizationTab]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        ORGANIZATION_PROJECT_SORT_STORAGE_KEY,
        projectSort,
      );
    } catch {
      // localStorage may be unavailable in some browser privacy modes.
    }
  }, [projectSort]);

  const projectRows = useMemo(
    () => getProjectRows(projectTree ?? [], user),
    [projectTree, user],
  );

  const summaryMetrics = summaryData?.metrics ?? {};
  const organizationProjectRows = useMemo(
    () => normalizeSummaryProjects(summaryData?.projects, projectRows),
    [projectRows, summaryData?.projects],
  );
  const sortedOrganizationProjectRows = useMemo(
    () => sortOrganizationProjects(organizationProjectRows, projectSort),
    [organizationProjectRows, projectSort],
  );
  const memberCount = toNumber(summaryMetrics.memberCount, user ? 1 : 0);
  const projectCount = toNumber(
    summaryMetrics.projectCount,
    organizationProjectRows.length,
  );
  const summaryOrganization = summaryData?.organization ?? null;
  const hasAssignedOrganization = Boolean(
    summaryData?.hasOrganization ||
      user?.organization_id ||
      summaryOrganization?.id,
  );
  const fallbackOrganizationName =
    summaryOrganization?.name ||
    user?.organization_name ||
    user?.organizationName ||
    user?.organization_label ||
    "현재 소속 조직";
  const childOrganizations = useMemo(
    () =>
      normalizeChildOrganizations(
        summaryData?.affiliations ?? summaryData?.children,
        fallbackOrganizationName,
      ),
    [
      fallbackOrganizationName,
      summaryData?.affiliations,
      summaryData?.children,
    ],
  );

  const currentOrganization = useMemo(
    () =>
      getCurrentOrganization({
        user,
        permission,
        summaryOrganization,
        projectCount,
        childCount: childOrganizations.length,
        memberCount,
      }),
    [
      childOrganizations.length,
      memberCount,
      permission,
      projectCount,
      summaryOrganization,
      user,
    ],
  );

  const currentOrganizationId = normalizeComparableId(
    currentOrganization?.organization_id ||
      user?.organization_id ||
      summaryOrganization?.id,
  );
  const currentOrganizationDescription =
    summaryOrganization?.storedDescription ?? summaryOrganization?.description ?? "";
  const ownOrganizationId = normalizeComparableId(user?.organization_id);
  const isViewingOwnOrganization = Boolean(
    ownOrganizationId && currentOrganizationId === ownOrganizationId,
  );
  const isOwnOrganizationProject = useCallback(
    (project) => {
      const projectOrganizationId = normalizeComparableId(
        project?.organizationId ?? project?.organization_id,
      );

      return Boolean(
        ownOrganizationId &&
          projectOrganizationId &&
          projectOrganizationId === ownOrganizationId,
      );
    },
    [ownOrganizationId],
  );
  const handleOpenProject = useCallback(
    (project) => {
      const projectId = project?.id ?? project?.project_id;
      if (!projectId) return;

      const projectPath = `/project/${encodeURIComponent(projectId)}`;

      if (isOwnOrganizationProject(project)) {
        navigate(projectPath);
        return;
      }

      window.open(
        `${window.location.origin}${projectPath}?organizationScope=child`,
        "_blank",
        "noopener,noreferrer",
      );
    },
    [isOwnOrganizationProject, navigate],
  );

  const handleProjectRowKeyDown = useCallback(
    (event, project) => {
      if (event.key !== "Enter" && event.key !== " ") return;

      event.preventDefault();
      handleOpenProject(project);
    },
    [handleOpenProject],
  );

  useEffect(() => {
    setOrganizationSettingsForm({
      name: currentOrganization?.name || "",
      description: currentOrganizationDescription || "",
    });
  }, [
    currentOrganization?.name,
    currentOrganizationDescription,
    currentOrganizationId,
  ]);
  const directChildOrganizations = useMemo(() => {
    if (!currentOrganizationId) {
      return childOrganizations.filter(
        (organization) => toNumber(organization.depth, 1) === 1,
      );
    }

    return childOrganizations.filter((organization) => {
      const parentId = normalizeComparableId(organization.parentId);
      return Boolean(parentId && parentId === currentOrganizationId);
    });
  }, [childOrganizations, currentOrganizationId]);

  const organizations = useMemo(
    () => [currentOrganization, ...directChildOrganizations],
    [currentOrganization, directChildOrganizations],
  );

  const selectedOrganization = useMemo(
    () =>
      organizations.find((organization) => organization.id === selectedOrgId) ||
      currentOrganization,
    [currentOrganization, organizations, selectedOrgId],
  );

  useEffect(() => {
    const currentOrganizationId = normalizeComparableId(
      currentOrganization?.organization_id || currentOrganization?.id,
    );

    if (
      !selectedOrganizationQueryId ||
      selectedOrganizationQueryId === currentOrganizationId
    ) {
      if (selectedOrgId !== ROOT_ORG_ID) setSelectedOrgId(ROOT_ORG_ID);
      return;
    }

    const matchedOrganization = organizations.find((organization) => {
      const organizationId = normalizeComparableId(
        organization.organization_id || organization.id,
      );
      return organizationId === selectedOrganizationQueryId;
    });

    if (matchedOrganization && selectedOrgId !== matchedOrganization.id) {
      setSelectedOrgId(matchedOrganization.id);
    }
  }, [
    currentOrganization?.id,
    currentOrganization?.organization_id,
    organizations,
    selectedOrgId,
    selectedOrganizationQueryId,
  ]);
  const permissionRows = useMemo(
    () =>
      ORGANIZATION_PERMISSIONS.map(([key, label]) => ({
        key,
        label,
        allowed: canUse(permission, organizationRole, appRole, key),
      })),
    [appRole, organizationRole, permission],
  );

  const allowedPermissionCount = permissionRows.filter(
    (row) => row.allowed,
  ).length;

  const memberRows = useMemo(
    () =>
      normalizeSummaryMembers(
        summaryData?.members,
        user,
        allowedPermissionCount,
      ),
    [allowedPermissionCount, summaryData?.members, user],
  );
  const summaryMemberRows = memberRows;
  const rootMemberRows = useMemo(
    () =>
      normalizeSummaryMembers(
        rootMemberSummaryData?.members,
        user,
        allowedPermissionCount,
      ),
    [allowedPermissionCount, rootMemberSummaryData?.members, user],
  );
  const settingsMemberRows = useMemo(
    () =>
      rootMemberSummaryData
        ? rootMemberRows
        : activeTab === "settings"
          ? []
          : memberRows,
    [activeTab, memberRows, rootMemberRows, rootMemberSummaryData],
  );
  const memberDirectoryRows = useMemo(
    () =>
      [...settingsMemberRows].sort((left, right) => {
        const leftIsCurrent =
          normalizeComparableId(left.organizationId) === currentOrganizationId;
        const rightIsCurrent =
          normalizeComparableId(right.organizationId) === currentOrganizationId;

        if (leftIsCurrent !== rightIsCurrent) return leftIsCurrent ? -1 : 1;

        const organizationComparison = String(
          left.organizationName ?? "",
        ).localeCompare(String(right.organizationName ?? ""), "ko");
        if (!leftIsCurrent && organizationComparison !== 0) {
          return organizationComparison;
        }

        return String(left.name ?? "").localeCompare(
          String(right.name ?? ""),
          "ko",
        );
      }),
    [currentOrganizationId, settingsMemberRows],
  );
  const currentUserId = normalizeComparableId(user?.id);
  const permissionTargetMemberRows = useMemo(() => {
    if (!currentUserId) return memberDirectoryRows;

    return memberDirectoryRows.filter(
      (member) => normalizeComparableId(member.id) !== currentUserId,
    );
  }, [currentUserId, memberDirectoryRows]);
  const activeMemberRows =
    activeTab === "settings" && settingsSection === "permissions"
      ? permissionTargetMemberRows
      : activeTab === "settings"
        ? memberDirectoryRows
        : memberRows;
  const isLoadingSettingsMembers =
    isLoadingSummary || isLoadingRootMembers;

  const filteredMemberRows = useMemo(() => {
    const query = memberQuery.trim().toLowerCase();
    if (!query) return activeMemberRows;

    return activeMemberRows.filter((member) =>
      [
        member.name,
        member.email,
        member.organizationName,
        member.organizationRole,
        formatDateTime(member.lastLoginAt),
        formatDateTime(member.latestActivityAt),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [activeMemberRows, memberQuery]);

  const selectedMember = useMemo(
    () =>
      activeMemberRows.find((member) => member.id === selectedMemberId) ?? null,
    [activeMemberRows, selectedMemberId],
  );

  useEffect(() => {
    if (activeMemberRows.length === 0) {
      if (selectedMemberId !== null) setSelectedMemberId(null);
      return;
    }

    if (!activeMemberRows.some((member) => member.id === selectedMemberId)) {
      setSelectedMemberId(activeMemberRows[0].id);
    }
  }, [activeMemberRows, selectedMemberId]);

  useEffect(() => {
    if (!selectedMember) {
      setDraftOrganizationRole("user");
      setDraftMemberPermission({});
      setMoveTargetOrganizationId("");
      return;
    }

    setDraftOrganizationRole(selectedMember.organizationRoleValue ?? "user");
    setDraftMemberPermission(parsePermissionValue(selectedMember.permission));
    setMoveTargetOrganizationId(
      normalizeComparableId(selectedMember.organizationId),
    );
  }, [selectedMember]);

  const organizationTreeRows = useMemo(() => {
    const currentTreeOrganizationId =
      currentOrganization?.organization_id ||
      summaryOrganization?.id ||
      currentOrganization?.id;
    const currentTreeOrganization = {
      ...currentOrganization,
      id: currentTreeOrganizationId || currentOrganization?.id,
      organization_id: currentTreeOrganizationId,
      parentId:
        summaryOrganization?.parentId ??
        summaryOrganization?.parent_id ??
        currentOrganization?.parentId ??
        null,
      parentName: currentOrganization?.parentName || "-",
      countryCode:
        summaryOrganization?.countryCode ??
        summaryOrganization?.country_code ??
        null,
      countryName: getCountryDisplayName({
        code:
          summaryOrganization?.countryCode ??
          summaryOrganization?.country_code ??
          null,
        country_name:
          summaryOrganization?.countryName ?? summaryOrganization?.country_name,
        default_name:
          summaryOrganization?.defaultCountryName ??
          summaryOrganization?.default_country_name,
      }),
      isActive: summaryOrganization?.isActive ?? true,
      source: "current",
    };

    return sortOrganizationTreeRows(
      [currentTreeOrganization, ...childOrganizations],
      currentTreeOrganizationId,
    );
  }, [childOrganizations, currentOrganization, summaryOrganization]);
  const organizationChartForest = useMemo(
    () =>
      buildOrganizationChartForest(
        organizationChart.organizations,
        organizationChart.members,
      ),
    [organizationChart.members, organizationChart.organizations],
  );
  const createOrganizationParent = useMemo(
    () =>
      organizationChart.organizations.find(
        (organization) =>
          getOrganizationActualId(organization) ===
          normalizeComparableId(createOrganizationForm.parent_id),
      ) ?? null,
    [createOrganizationForm.parent_id, organizationChart.organizations],
  );
  const rootRoleOrganization = useMemo(
    () =>
      organizationChart.organizations.find(
        (organization) =>
          getOrganizationActualId(organization) ===
          organizationChart.rootOrganizationId,
      ) ?? organizationChart.organizations[0] ?? null,
    [organizationChart.organizations, organizationChart.rootOrganizationId],
  );
  const manageableChartOrganizationIds = useMemo(() => {
    if (!organizationChart.rootOrganizationId) return new Set();

    return new Set(
      organizationChart.organizations
        .map(getOrganizationActualId)
        .filter(Boolean),
    );
  }, [organizationChart.organizations, organizationChart.rootOrganizationId]);
  const memberMoveOrganizationRows = useMemo(
    () =>
      sortOrganizationTreeRows(
        organizationChart.organizations,
        organizationChart.rootOrganizationId,
      ).filter((organization) => {
        const organizationId = getOrganizationActualId(organization);
        return (
          Boolean(organizationId) &&
          organization.status !== "inactive" &&
          organization.isActive !== false
        );
      }),
    [organizationChart.organizations, organizationChart.rootOrganizationId],
  );
  const selectedMemberOrganizationId = normalizeComparableId(
    selectedMember?.organizationId,
  );
  const selectedMemberOrganization = memberMoveOrganizationRows.find(
    (organization) =>
      getOrganizationActualId(organization) === selectedMemberOrganizationId,
  );
  const selectedMoveOrganization = memberMoveOrganizationRows.find(
    (organization) =>
      getOrganizationActualId(organization) === moveTargetOrganizationId,
  );

  const organizationBreadcrumbRows = useMemo(() => {
    const responsePath = normalizeOrganizationPath(summaryOrganization?.path);
    if (responsePath.length > 0) return responsePath;

    const rowById = new Map();
    organizationTreeRows.forEach((row) => {
      const organizationId = getOrganizationActualId(row);
      if (!organizationId) return;
      rowById.set(organizationId, row);
    });

    const currentId = normalizeComparableId(
      currentOrganization?.organization_id ||
        summaryOrganization?.id ||
        currentOrganization?.id,
    );
    const path = [];
    const visited = new Set();
    let cursor = currentId ? rowById.get(currentId) : null;

    while (cursor) {
      const organizationId = getOrganizationActualId(cursor);
      if (!organizationId || visited.has(organizationId)) break;
      visited.add(organizationId);
      path.unshift({
        id: organizationId,
        organization_id: cursor.organization_id || cursor.id,
        parentId: cursor.parentId ?? null,
        name: cursor.name ?? "-",
      });
      const parentId = normalizeComparableId(cursor.parentId);
      cursor = parentId ? rowById.get(parentId) : null;
    }

    return path.length > 0
      ? path
      : [
          {
            id: currentId || ROOT_ORG_ID,
            organization_id:
              currentOrganization?.organization_id ?? summaryOrganization?.id,
            parentId: currentOrganization?.parentId ?? null,
            name: currentOrganization?.name ?? selectedOrganization?.name ?? "-",
          },
        ];
  }, [
    currentOrganization,
    organizationTreeRows,
    selectedOrganization?.name,
    summaryOrganization,
  ]);
  const canShowOrganizationBreadcrumb =
    canViewChildOrganizations && organizationBreadcrumbRows.length > 1;

  const handleSelectOrganizationRow = useCallback(
    (organization) => {
      const organizationId = normalizeComparableId(
        organization?.organization_id || organization?.id,
      );
      if (!organizationId) return;

      navigate(
        `/organization?organization_id=${encodeURIComponent(organizationId)}`,
      );
    },
    [navigate],
  );

  const canUpdateMember = canManageOrganizationUser(
    permission,
    organizationRole,
    appRole,
  );
  const canCreateMember = canRequestOrganizationUser(
    permission,
    organizationRole,
    appRole,
  );
  const canRemoveMember = canRemoveOrganizationUser(
    permission,
    organizationRole,
    appRole,
  );
  const canMoveMember = canMoveOrganizationUser(
    permission,
    organizationRole,
    appRole,
  );
  const canCreateOrganizationNode = canCreateOrganization(
    permission,
    organizationRole,
    appRole,
  );
  const canDeleteOrganizationNode = canDeleteOrganizationAffiliation(
    permission,
    organizationRole,
    appRole,
  );
  const canCreateChildOrganizationAt = useCallback(
    (organization) => {
      const organizationId = getOrganizationActualId(organization);
      return Boolean(
        canCreateOrganizationNode &&
          organizationId &&
          manageableChartOrganizationIds.has(organizationId),
      );
    },
    [canCreateOrganizationNode, manageableChartOrganizationIds],
  );
  const canDeleteChildOrganizationAt = useCallback(
    (organization) => {
      const organizationId = getOrganizationActualId(organization);
      return Boolean(
        canDeleteOrganizationNode &&
          organizationId &&
          organizationId !==
            normalizeComparableId(organizationChart.rootOrganizationId) &&
          normalizeComparableId(organization?.parentId),
      );
    }, [canDeleteOrganizationNode, organizationChart.rootOrganizationId],
  );
  const canCreateRootOrganizationNode = canCreateRootOrganization(
    permission,
    appRole,
  );
  const organizationSettingsTargetId =
    currentOrganization?.organization_id || summaryOrganization?.id || "";
  const organizationSettingsHasChanges =
    organizationSettingsForm.name.trim() !==
      String(currentOrganization?.name ?? "").trim() ||
    organizationSettingsForm.description.trim() !==
      String(currentOrganizationDescription ?? "").trim();
  const editableOrganizationRoleOptions = useMemo(
    () =>
      canAssignMasterRole
        ? ORGANIZATION_ROLE_OPTIONS
        : ORGANIZATION_ROLE_OPTIONS.filter((role) => role.value !== "master"),
    [canAssignMasterRole],
  );
  const selectedMemberIsMaster =
    selectedMember?.appRoleValue === "master" ||
    selectedMember?.organizationRoleValue === "master";
  const selectedMemberIsCurrentUser =
    Boolean(selectedMember) &&
    currentUserId &&
    normalizeComparableId(selectedMember.id) === currentUserId;
  const canEditSelectedMember =
    canUpdateMember &&
    !selectedMemberIsCurrentUser &&
    (canAssignMasterRole || !selectedMemberIsMaster);
  const canRemoveSelectedMember =
    Boolean(selectedMember) &&
    canRemoveMember &&
    !selectedMemberIsCurrentUser &&
    (canAssignMasterRole || !selectedMemberIsMaster);
  const canMoveSelectedMember =
    Boolean(selectedMember) &&
    canMoveMember &&
    (!selectedMemberIsCurrentUser || isSystemMaster) &&
    (canAssignMasterRole || !selectedMemberIsMaster);
  const manageableMemberById = useMemo(
    () =>
      new Map(
        memberDirectoryRows.map((member) => [
          normalizeComparableId(member.id),
          member,
        ]),
      ),
    [memberDirectoryRows],
  );
  const canManageChartMember = useCallback(
    (chartMember) => {
      const member = manageableMemberById.get(
        normalizeComparableId(chartMember?.id),
      );
      if (!member || !canMoveMember) return false;

      const isCurrentUser =
        normalizeComparableId(member.id) === currentUserId;
      const isMaster =
        member.appRoleValue === "master" ||
        member.organizationRoleValue === "master";
      return (
        (!isCurrentUser || isSystemMaster) &&
        (canAssignMasterRole || !isMaster)
      );
    },
    [
      canAssignMasterRole,
      canMoveMember,
      currentUserId,
      isSystemMaster,
      manageableMemberById,
    ],
  );
  const handleManageChartMember = useCallback(
    (chartMember) => {
      const member = manageableMemberById.get(
        normalizeComparableId(chartMember?.id),
      );
      if (!member || !canManageChartMember(chartMember)) return;

      setSelectedMemberId(member.id);
      setMemberQuery("");
      handleOrganizationTabChange("members");
    },
    [
      canManageChartMember,
      handleOrganizationTabChange,
      manageableMemberById,
    ],
  );
  const selectedMemberOrganizationHasChanges =
    canMoveSelectedMember &&
    Boolean(moveTargetOrganizationId) &&
    moveTargetOrganizationId !== selectedMemberOrganizationId;
  const canEditSelectedPermissions =
    canEditSelectedMember &&
    (draftOrganizationRole === "admin" || draftOrganizationRole === "user");
  const visibleOrganizationPermissionRows = useMemo(
    () =>
      ORGANIZATION_PERMISSIONS.filter(
        ([key]) =>
          !HIDDEN_PERMISSION_SETTING_KEYS.has(key) &&
          (canAssignMasterRole || ADMIN_PERMISSION_KEYS.has(key)),
      ),
    [canAssignMasterRole],
  );
  const selectedMemberPermissionRows = useMemo(
    () =>
      visibleOrganizationPermissionRows
        .filter(
          ([key]) =>
            draftOrganizationRole !== "user" ||
            key === PERM.ORGANIZATION_NOTICE_WRITE,
        )
        .map(([key, label]) => ({
          key,
          label,
          allowed:
            key === "organization.read_children"
              ? draftMemberPermission?.["organization.read_children"] ===
                  true ||
                draftMemberPermission?.[
                  "organization.user.read_children"
                ] === true
              : draftMemberPermission?.[key] === true,
        })),
    [
      draftMemberPermission,
      draftOrganizationRole,
      visibleOrganizationPermissionRows,
    ],
  );
  const selectedMemberAllowedCount = selectedMemberPermissionRows.filter(
    (row) => row.allowed,
  ).length;
  const selectedMemberHasChanges =
    Boolean(selectedMember) &&
    canEditSelectedMember &&
    (draftOrganizationRole !== selectedMember.organizationRoleValue ||
      !arePermissionDraftsEqual(
        draftMemberPermission,
        selectedMember.permission,
      ));

  const handleDraftOrganizationRoleChange = useCallback((value) => {
    const nextRole = normalizeRoleValue(value);
    setDraftOrganizationRole(nextRole);
    setDraftMemberPermission((current) =>
      buildPermissionDraftForOrganizationRole(nextRole, current),
    );
  }, []);

  const handleToggleSelectedPermission = useCallback(
    (key) => {
      if (!canEditSelectedPermissions) return;
      if (!canAssignMasterRole && !ADMIN_PERMISSION_KEYS.has(key)) return;
      if (
        draftOrganizationRole === "user" &&
        key !== PERM.ORGANIZATION_NOTICE_WRITE
      ) {
        return;
      }

      setDraftMemberPermission((current) => {
        const isChildReadPermission = key === "organization.read_children";
        const currentAllowed = isChildReadPermission
          ? current?.["organization.read_children"] === true ||
            current?.["organization.user.read_children"] === true
          : current?.[key] === true;
        const nextAllowed = !currentAllowed;

        return {
          ...current,
          [key]: nextAllowed,
          ...(isChildReadPermission
            ? { "organization.user.read_children": nextAllowed }
            : {}),
        };
      });
    },
    [
      canAssignMasterRole,
      canEditSelectedPermissions,
      draftOrganizationRole,
    ],
  );

  const handleSaveSelectedMember = useCallback(async () => {
    if (!selectedMember) return;

    if (selectedMemberIsCurrentUser) {
      notify("자신의 권한은 직접 수정할 수 없습니다.", false);
      return;
    }

    if (!canEditSelectedMember) {
      notify("사용자 권한을 수정할 수 없습니다.", false);
      return;
    }

    if (!selectedMemberHasChanges) {
      notify("변경된 사용자 권한이 없습니다.");
      return;
    }

    setSavingMember(true);
    const permissionPayload = parsePermissionValue(draftMemberPermission);
    const result = await updateOrganizationUserPermission(selectedMember.id, {
      organization_role: draftOrganizationRole,
      permission: permissionPayload,
    });
    setSavingMember(false);

    if (!result.success) {
      notify(result.message || "사용자 권한 저장에 실패했습니다.", false);
      return;
    }

    notify(result.message || "사용자 권한이 저장되었습니다.");
    await Promise.all([loadOrganizationSummary(), loadOrganizationChart()]);
  }, [
    canEditSelectedMember,
    draftMemberPermission,
    draftOrganizationRole,
    loadOrganizationSummary,
    loadOrganizationChart,
    notify,
    selectedMember,
    selectedMemberIsCurrentUser,
    selectedMemberHasChanges,
  ]);

  const handleMoveSelectedMember = useCallback(async () => {
    if (!selectedMember) return;
    if (!canMoveSelectedMember) {
      notify("구성원의 소속 조직을 이동할 권한이 없습니다.", false);
      return;
    }
    if (
      !selectedMemberOrganizationHasChanges ||
      !selectedMoveOrganization ||
      !currentOrganizationId
    ) {
      notify("이동할 소속을 선택해 주세요.", false);
      return;
    }

    const sourceOrganizationName =
      selectedMemberOrganization?.name ||
      selectedOrganization?.name ||
      "현재 조직";
    const confirmed = window.confirm(
      `${selectedMember.name}님의 소속을\n${sourceOrganizationName} → ${selectedMoveOrganization.name}(으)로 이동하시겠습니까?`,
    );
    if (!confirmed) return;

    setMovingMember(true);
    const result = await moveOrganizationUser(selectedMember.id, {
      managed_organization_id:
        normalizeComparableId(organizationChart.rootOrganizationId) ||
        currentOrganizationId,
      organization_id: getOrganizationActualId(selectedMoveOrganization),
    });
    setMovingMember(false);

    if (!result.success) {
      notify(
        result.message || "구성원의 소속 조직을 이동하지 못했습니다.",
        false,
      );
      return;
    }

    notify(result.message || "구성원의 소속 조직을 이동했습니다.");
    setSelectedMemberId(null);

    if (selectedMemberIsCurrentUser) {
      try {
        await refreshAuth?.();
      } catch {
        // The following me() call still reads the current user from the server.
      }

      try {
        const nextUser = await loadAuthenticatedUser?.();
        if (nextUser) setAuthenticated(nextUser);
      } catch {
        // Navigation below will surface any remaining auth issue.
      }

      navigate("/organization?tab=settings&section=members", { replace: true });
      return;
    }

    await Promise.all([loadOrganizationSummary(), loadOrganizationChart()]);
  }, [
    canMoveSelectedMember,
    currentOrganizationId,
    loadAuthenticatedUser,
    loadOrganizationChart,
    loadOrganizationSummary,
    navigate,
    notify,
    organizationChart.rootOrganizationId,
    refreshAuth,
    selectedMember,
    selectedMemberIsCurrentUser,
    selectedMemberOrganization,
    selectedMemberOrganizationHasChanges,
    selectedMoveOrganization,
    selectedOrganization?.name,
    setAuthenticated,
  ]);

  const handleCreateUserFormChange = useCallback((field, value) => {
    setCreateUserForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const resetCreateUserForm = useCallback(() => {
    setCreateUserForm(INITIAL_CREATE_USER_FORM);
  }, []);

  const handleOpenCreateUser = useCallback(() => {
    if (!canCreateMember) {
      notify("구성원 추가 권한이 없습니다.", false);
      return;
    }

    resetCreateUserForm();
    setIsCreateUserOpen(true);
  }, [canCreateMember, notify, resetCreateUserForm]);

  const handleCloseCreateUser = useCallback(() => {
    if (isSubmittingCreateUser) return;
    setIsCreateUserOpen(false);
  }, [isSubmittingCreateUser]);

  const handleSubmitCreateUser = useCallback(
    async (event) => {
      event.preventDefault();

      if (!canCreateMember) {
        notify("구성원 추가 권한이 없습니다.", false);
        return;
      }

      const email = createUserForm.email.trim();

      if (!email) {
        notify("초대할 사용자의 이메일을 입력해 주세요.", false);
        return;
      }

      setIsSubmittingCreateUser(true);
      const targetOrganizationId =
        selectedOrganization?.organization_id ||
        (selectedOrganization?.id !== ROOT_ORG_ID
          ? selectedOrganization?.id
          : null) ||
        currentOrganization?.organization_id ||
        summaryOrganization?.id ||
        user?.organization_id ||
        null;
      const result = await createOrganizationInvitation({
        email,
        organization_id: targetOrganizationId,
      });
      setIsSubmittingCreateUser(false);

      if (!result.success) {
        notify(result.message || "조직 초대를 보내지 못했습니다.", false);
        return;
      }

      notify(result.message || "조직 초대를 보냈습니다.");
      setIsCreateUserOpen(false);
      resetCreateUserForm();
    },
    [
      canCreateMember,
      createUserForm,
      currentOrganization?.organization_id,
      notify,
      resetCreateUserForm,
      selectedOrganization?.id,
      selectedOrganization?.organization_id,
      summaryOrganization?.id,
      user?.organization_id,
    ],
  );

  const handleCloseRemoveMemberConfirm = useCallback(() => {
    if (removingMember) return;
    setIsRemoveMemberConfirmOpen(false);
  }, [removingMember]);

  const handleRemoveSelectedMember = useCallback(() => {
    if (!selectedMember) return;

    if (selectedMemberIsCurrentUser) {
      notify("자신은 조직에서 제외할 수 없습니다.", false);
      return;
    }

    if (!canRemoveSelectedMember) {
      notify("사용자를 조직에서 제외할 권한이 없습니다.", false);
      return;
    }

    setIsRemoveMemberConfirmOpen(true);
  }, [
    canRemoveSelectedMember,
    notify,
    selectedMember,
    selectedMemberIsCurrentUser,
  ]);

  const handleConfirmRemoveSelectedMember = useCallback(async () => {
    if (!selectedMember) {
      setIsRemoveMemberConfirmOpen(false);
      return;
    }

    if (selectedMemberIsCurrentUser) {
      setIsRemoveMemberConfirmOpen(false);
      notify("자신은 조직에서 제외할 수 없습니다.", false);
      return;
    }

    if (!canRemoveSelectedMember) {
      setIsRemoveMemberConfirmOpen(false);
      notify("사용자를 조직에서 제외할 권한이 없습니다.", false);
      return;
    }

    setRemovingMember(true);
    const result = await removeOrganizationUser(selectedMember.id);
    setRemovingMember(false);

    if (!result.success) {
      notify(result.message || "사용자를 조직에서 제외하지 못했습니다.", false);
      return;
    }

    setIsRemoveMemberConfirmOpen(false);
    notify(result.message || "사용자를 조직에서 제외했습니다.");
    setSelectedMemberId(null);
    await Promise.all([loadOrganizationSummary(), loadOrganizationChart()]);
  }, [
    canRemoveSelectedMember,
    loadOrganizationChart,
    loadOrganizationSummary,
    notify,
    selectedMember,
    selectedMemberIsCurrentUser,
  ]);

  const handleOpenDeleteOrganization = useCallback(
    (organization) => {
      if (!canDeleteChildOrganizationAt(organization)) {
        notify("이 소속을 삭제할 권한이 없습니다.", false);
        return;
      }

      setDeleteOrganizationTarget(organization);
    },
    [canDeleteChildOrganizationAt, notify],
  );

  const handleCloseDeleteOrganization = useCallback(() => {
    if (isDeletingOrganization) return;
    setDeleteOrganizationTarget(null);
  }, [isDeletingOrganization]);

  const handleConfirmDeleteOrganization = useCallback(async () => {
    const targetOrganizationId = getOrganizationActualId(
      deleteOrganizationTarget,
    );
    if (
      !targetOrganizationId ||
      !canDeleteChildOrganizationAt(deleteOrganizationTarget) ||
      isDeletingOrganization
    ) {
      return;
    }

    setIsDeletingOrganization(true);
    const result = await deleteOrganizationAffiliation(targetOrganizationId);
    setIsDeletingOrganization(false);

    if (!result.success) {
      notify(result.message || "하위 소속을 삭제하지 못했습니다.", false);
      return;
    }

    setDeleteOrganizationTarget(null);
    notify(result.message || "하위 소속이 삭제되었습니다.");

    const rootOrganizationId = normalizeComparableId(
      result.data?.rootOrganizationId || organizationChart.rootOrganizationId,
    );
    const deletedOwnOrganization =
      targetOrganizationId === normalizeComparableId(user?.organization_id);
    const deletedViewedOrganization =
      targetOrganizationId === currentOrganizationId ||
      targetOrganizationId === selectedOrganizationQueryId;

    if (deletedOwnOrganization) {
      try {
        await refreshAuth?.();
        const nextUser = await loadAuthenticatedUser?.();
        if (nextUser) setAuthenticated(nextUser);
      } catch {
        // The chart reload below reflects the server-side organization move.
      }
    }

    await loadOrganizationChart();

    if (
      (deletedOwnOrganization || deletedViewedOrganization) &&
      rootOrganizationId
    ) {
      navigate(
        `/organization?organization_id=${encodeURIComponent(rootOrganizationId)}&tab=chart`,
        { replace: true },
      );
      return;
    }

    await loadOrganizationSummary();
  }, [
    canDeleteChildOrganizationAt,
    currentOrganizationId,
    deleteOrganizationTarget,
    isDeletingOrganization,
    loadAuthenticatedUser,
    loadOrganizationChart,
    loadOrganizationSummary,
    navigate,
    notify,
    organizationChart.rootOrganizationId,
    refreshAuth,
    selectedOrganizationQueryId,
    setAuthenticated,
    user?.organization_id,
  ]);

  const handleCreateOrganizationFormChange = useCallback((field, value) => {
    setCreateOrganizationForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const resetCreateOrganizationForm = useCallback((parentId = "") => {
    setCreateOrganizationForm({
      ...INITIAL_CREATE_ORGANIZATION_FORM,
      parent_id: parentId,
    });
  }, []);

  const handleOpenCreateOrganization = useCallback((parentOrganization) => {
    if (!canCreateChildOrganizationAt(parentOrganization)) {
      notify("이 위치에 소속을 생성할 권한이 없습니다.", false);
      return;
    }

    const parentId = getOrganizationActualId(parentOrganization);

    if (!parentId) {
      notify("최상위 조직 정보를 찾을 수 없습니다.", false);
      return;
    }

    resetCreateOrganizationForm(parentId);
    setIsCreateOrganizationOpen(true);
  }, [
    canCreateChildOrganizationAt,
    notify,
    resetCreateOrganizationForm,
  ]);

  const handleCloseCreateOrganization = useCallback(() => {
    if (isSubmittingCreateOrganization) return;
    setIsCreateOrganizationOpen(false);
  }, [isSubmittingCreateOrganization]);

  const handleSubmitCreateOrganization = useCallback(
    async (event) => {
      event.preventDefault();

      const parentId = normalizeComparableId(
        createOrganizationForm.parent_id,
      );
      const name = createOrganizationForm.name.trim();
      const countryCode = createOrganizationForm.country_code.trim();
      const description = createOrganizationForm.description.trim();

      if (!parentId) {
        notify("최상위 조직을 확인해 주세요.", false);
        return;
      }

      if (
        !canCreateOrganizationNode ||
        !manageableChartOrganizationIds.has(parentId)
      ) {
        notify("이 위치에 소속을 생성할 권한이 없습니다.", false);
        return;
      }

      if (!name) {
        notify("소속명을 입력해 주세요.", false);
        return;
      }

      if (name.length > 100) {
        notify("소속명은 100자 이하로 입력해 주세요.", false);
        return;
      }

      if (countryCode.length > 10) {
        notify("국가는 10자 이하로 입력해 주세요.", false);
        return;
      }

      setIsSubmittingCreateOrganization(true);
      const result = await createChildOrganization({
        parent_id: parentId,
        name,
        country_code: countryCode || null,
        description,
      });
      setIsSubmittingCreateOrganization(false);

      if (!result.success) {
        notify(result.message || "소속 생성에 실패했습니다.", false);
        return;
      }

      notify(result.message || "소속이 생성되었습니다.");
      setIsCreateOrganizationOpen(false);
      resetCreateOrganizationForm(parentId);
      await Promise.all([loadOrganizationSummary(), loadOrganizationChart()]);
      setSelectedOrgId(ROOT_ORG_ID);
      handleOrganizationTabChange("chart");
    },
    [
      canCreateOrganizationNode,
      createOrganizationForm,
      handleOrganizationTabChange,
      loadOrganizationChart,
      loadOrganizationSummary,
      manageableChartOrganizationIds,
      notify,
      resetCreateOrganizationForm,
    ],
  );

  const handleRootOrganizationFormChange = useCallback((field, value) => {
    setRootOrganizationForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleOrganizationSettingsFormChange = useCallback((field, value) => {
    setOrganizationSettingsForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleSubmitOrganizationSettings = useCallback(
    async (event) => {
      event.preventDefault();

      if (!canUpdateOrganizationSettings) {
        notify("조직 수정 권한이 없습니다.", false);
        return;
      }

      if (!organizationSettingsTargetId) {
        notify("조직 정보가 없습니다.", false);
        return;
      }

      const name = organizationSettingsForm.name.trim();
      const description = organizationSettingsForm.description.trim();

      if (!name) {
        notify("조직명을 입력해 주세요.", false);
        return;
      }

      if (name.length > 100) {
        notify("조직명은 100자 이하로 입력해 주세요.", false);
        return;
      }

      if (description.length > 1000) {
        notify("조직 설명은 1000자 이하로 입력해 주세요.", false);
        return;
      }

      if (!organizationSettingsHasChanges) {
        notify("변경된 조직 설정이 없습니다.");
        return;
      }

      setIsSavingOrganizationSettings(true);
      const result = await updateOrganization(organizationSettingsTargetId, {
        name,
        description,
      });
      setIsSavingOrganizationSettings(false);

      if (!result.success) {
        notify(result.message || "조직 설정 저장에 실패했습니다.", false);
        return;
      }

      notify(result.message || "조직 설정이 저장되었습니다.");

      if (isViewingOwnOrganization) {
        try {
          await refreshAuth?.();
        } catch {
          // The following me() call still reads the current user from the server.
        }

        try {
          const nextUser = await loadAuthenticatedUser?.();
          if (nextUser) setAuthenticated(nextUser);
        } catch {
          // Summary reload below will surface any remaining auth issue.
        }
      }

      await Promise.all([loadOrganizationSummary(), loadOrganizationChart()]);
    },
    [
      canUpdateOrganizationSettings,
      isViewingOwnOrganization,
      loadAuthenticatedUser,
      loadOrganizationChart,
      loadOrganizationSummary,
      notify,
      organizationSettingsForm,
      organizationSettingsHasChanges,
      organizationSettingsTargetId,
      refreshAuth,
      setAuthenticated,
    ],
  );

  const handleSubmitRootOrganization = useCallback(
    async (event) => {
      event.preventDefault();

      if (!canCreateRootOrganizationNode) {
        notify(
          "조직 생성 권한이 없습니다. 조직 관리자에게 조직 초대를 요청하세요.",
          false,
        );
        return;
      }

      const name = rootOrganizationForm.name.trim();
      const countryCode = rootOrganizationForm.country_code.trim();
      const description = rootOrganizationForm.description.trim();

      if (!name) {
        notify("조직명을 입력해 주세요.", false);
        return;
      }

      if (name.length > 100) {
        notify("조직명은 100자 이하로 입력해 주세요.", false);
        return;
      }

      if (countryCode.length > 10) {
        notify("국가는 10자 이하로 입력해 주세요.", false);
        return;
      }

      setIsSubmittingRootOrganization(true);
      const result = await createOrganization({
        name,
        country_code: countryCode || null,
        description,
      });
      setIsSubmittingRootOrganization(false);

      if (!result.success) {
        notify(result.message || "조직 생성에 실패했습니다.", false);
        return;
      }

      notify(result.message || "조직이 생성되었습니다.");
      setRootOrganizationForm(INITIAL_ROOT_ORGANIZATION_FORM);

      try {
        await refreshAuth?.();
      } catch {
        // The following me() call still reads the current user from the server.
      }

      try {
        const nextUser = await loadAuthenticatedUser?.();
        if (nextUser) setAuthenticated(nextUser);
      } catch {
        // Summary reload below will surface any remaining auth issue.
      }

      await loadOrganizationSummary();
      setSelectedOrgId(ROOT_ORG_ID);
      setActiveTab("summary");
    },
    [
      canCreateRootOrganizationNode,
      loadAuthenticatedUser,
      loadOrganizationSummary,
      notify,
      refreshAuth,
      rootOrganizationForm,
      setAuthenticated,
    ],
  );

  const handleRespondInvitation = useCallback(
    async (invitationId, action) => {
      if (!invitationId || respondingInvitationId) return;

      setRespondingInvitationId(`${action}:${invitationId}`);
      const result = await respondOrganizationInvitation(invitationId, action);
      setRespondingInvitationId("");

      if (!result.success) {
        notify(result.message || "조직 초대 처리에 실패했습니다.", false);
        return;
      }

      notify(
        result.message ||
          (action === "accept"
            ? "조직 초대를 수락했습니다."
            : "조직 초대를 거절했습니다."),
      );
      requestGlobalNotificationsRefresh();
      await loadReceivedInvitations();

      if (action === "accept") {
        try {
          await refreshAuth?.();
        } catch {
          // The following me() call still reads the current user from the server.
        }

        try {
          const nextUser = await loadAuthenticatedUser?.();
          if (nextUser) setAuthenticated(nextUser);
        } catch {
          // Summary reload below will surface any remaining auth issue.
        }

        await loadOrganizationSummary();
        setSelectedOrgId(ROOT_ORG_ID);
        setActiveTab("summary");
      }
    },
    [
      loadAuthenticatedUser,
      loadOrganizationSummary,
      loadReceivedInvitations,
      notify,
      refreshAuth,
      respondingInvitationId,
      setAuthenticated,
    ],
  );

  const renderOrganizationLoading = (
    label = "데이터를 불러오는 중입니다.",
    compact = false,
  ) => (
    <div
      className={`organizationPage__loadingState ${
        compact ? "isCompact" : ""
      }`}
      role="status"
    >
      <span aria-hidden="true" className="organizationPage__loadingSpinner" />
      <strong>{label}</strong>
    </div>
  );

  const renderMemberListPanel = (withCreateAction = false) => (
    <article className="organizationPage__panel organizationPage__memberListPanel">
      <div className="organizationPage__memberTools">
        <label className="organizationPage__searchBox">
          <Search size={15} aria-hidden="true" />
          <input
            aria-label="구성원 검색"
            onChange={(event) => setMemberQuery(event.target.value)}
            placeholder="이름, 이메일, 역할 검색"
            type="search"
            value={memberQuery}
          />
        </label>
        {withCreateAction && canCreateMember && (
          <button
            className="organizationPage__createBtn"
            onClick={handleOpenCreateUser}
            type="button"
          >
            <UserPlus size={15} aria-hidden="true" />
            <span>구성원 추가</span>
          </button>
        )}
      </div>
      {isLoadingSettingsMembers ? (
        renderOrganizationLoading("구성원 정보를 불러오는 중입니다.", true)
      ) : rootMemberSummaryError ? (
        <div className="organizationPage__emptyState isCompact">
          <UsersRound size={28} aria-hidden="true" />
          <strong>{rootMemberSummaryError}</strong>
        </div>
      ) : filteredMemberRows.length > 0 ? (
        <div className="organizationPage__tableWrap">
          <table className="organizationPage__table organizationPage__table--selectable">
            <thead>
              <tr>
                <th>구성원</th>
                <th>소속</th>
                <th>조직 역할</th>
                <th>최근 접속</th>
                <th>최근 활동</th>
              </tr>
            </thead>
            <tbody>
              {filteredMemberRows.map((member) => {
                return (
                  <tr
                    className={
                      selectedMemberId === member.id ? "isSelected" : ""
                    }
                    key={member.id}
                    onClick={() => setSelectedMemberId(member.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        setSelectedMemberId(member.id);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td>
                      <span className="organizationPage__memberNameLine">
                        <strong className="organizationPage__tableMain">
                          {member.name}
                        </strong>
                      </span>
                      <span className="organizationPage__tableSub">
                        {member.email}
                      </span>
                    </td>
                    <td>{member.organizationName || "-"}</td>
                    <td>{member.organizationRole}</td>
                    <td>{formatDateTime(member.lastLoginAt)}</td>
                    <td>{formatDateTime(member.latestActivityAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="organizationPage__emptyState isCompact">
          <UsersRound size={28} aria-hidden="true" />
          <strong>조회된 구성원이 없습니다.</strong>
        </div>
      )}
    </article>
  );

  const renderMemberProfile = () => (
    <section className="organizationPage__memberProfile">
      <strong>{selectedMember.name}</strong>
      <span>{selectedMember.email}</span>
      <div className="organizationPage__detailList">
        <div>
          <small>조직 역할</small>
          <b>{selectedMember.organizationRole}</b>
        </div>
        <div>
          <small>생성일</small>
          <b>{formatDateTime(selectedMember.createdAt)}</b>
        </div>
        <div>
          <small>최근 로그인</small>
          <b>{formatDateTime(selectedMember.lastLoginAt)}</b>
        </div>
        <div>
          <small>최근 활동</small>
          <b>{formatDateTime(selectedMember.latestActivityAt)}</b>
        </div>
      </div>
    </section>
  );

  const renderMemberOrganizationMoveBlock = () => {
    if (!canMoveSelectedMember) return null;

    return (
      <section className="organizationPage__detailBlock organizationPage__memberOrganizationMove">
        <div className="organizationPage__detailTitle">
          <strong>소속 조직 이동</strong>
          <span>조직 이동 권한</span>
        </div>
        <div className="organizationPage__memberOrganizationCurrent">
          <Building2 size={17} aria-hidden="true" />
          <span>
            <small>현재 소속</small>
            <strong>
              {selectedMemberOrganization?.name ||
                selectedOrganization?.name ||
                "-"}
            </strong>
          </span>
        </div>
        <div
          aria-label="이동할 소속 조직"
          className="organizationPage__memberOrganizationTree"
          role="radiogroup"
        >
          {memberMoveOrganizationRows.map((organization) => {
            const organizationId = getOrganizationActualId(organization);
            const isCurrentOrganization =
              organizationId === selectedMemberOrganizationId;
            const depth = Math.min(toNumber(organization.depth), 6);

            return (
              <label
                className={`${isCurrentOrganization ? "isCurrent" : ""} ${
                  organizationId === moveTargetOrganizationId
                    ? "isSelected"
                    : ""
                }`.trim()}
                key={organizationId}
                style={{ "--member-org-depth": `${depth * 18}px` }}
              >
                <input
                  checked={organizationId === moveTargetOrganizationId}
                  disabled={movingMember}
                  name={`member-organization-${selectedMember.id}`}
                  onChange={() => setMoveTargetOrganizationId(organizationId)}
                  type="radio"
                  value={organizationId}
                />
                <span className="organizationPage__memberOrganizationNode">
                  <i aria-hidden="true" />
                  <Building2 size={15} aria-hidden="true" />
                  <span>
                    <strong>{organization.name}</strong>
                    <small>
                      {isCurrentOrganization
                        ? "현재 소속"
                        : depth === 0
                          ? "현재 관리 조직"
                          : `${organization.parentName || "최상위 조직"} 내부 소속`}
                    </small>
                  </span>
                </span>
              </label>
            );
          })}
        </div>
        {memberMoveOrganizationRows.length <= 1 && (
          <p className="organizationPage__memberOrganizationNote">
            이동 가능한 활성 소속이 없습니다.
          </p>
        )}
        <div className="organizationPage__memberOrganizationActions">
          <span>
            조직 역할과 개별 권한은 유지되며 소속 조직만 변경됩니다.
          </span>
          <button
            className="organizationPage__saveBtn"
            disabled={
              !selectedMemberOrganizationHasChanges || movingMember
            }
            onClick={handleMoveSelectedMember}
            type="button"
          >
            {movingMember ? (
              <LoaderCircle
                aria-hidden="true"
                className="isSpinning"
                size={15}
              />
            ) : (
              <ArrowRightLeft size={15} aria-hidden="true" />
            )}
            <span>{movingMember ? "이동 중" : "선택 조직으로 이동"}</span>
          </button>
        </div>
      </section>
    );
  };

  const renderPermissionMemberDetails = () => (
    <dl className="organizationPage__detailList organizationPage__detailList--permission">
      <div>
        <dt>구성원</dt>
        <dd>
          {selectedMember.name || "-"} / {selectedMember.email || "-"}
        </dd>
      </div>
      <div>
        <dt>역할 적용 기준</dt>
        <dd>{rootRoleOrganization?.name || "최상위 조직"}</dd>
      </div>
      <div>
        <dt>현재 소속</dt>
        <dd>{selectedMember.organizationName || "-"}</dd>
      </div>
      <div>
        <dt>조직 역할</dt>
        <dd>{selectedMember.organizationRole}</dd>
      </div>
      <div>
        <dt>생성일</dt>
        <dd>{formatDateTime(selectedMember.createdAt)}</dd>
      </div>
      <div>
        <dt>최근 로그인</dt>
        <dd>{formatDateTime(selectedMember.lastLoginAt)}</dd>
      </div>
      <div>
        <dt>최근 활동</dt>
        <dd>{formatDateTime(selectedMember.latestActivityAt)}</dd>
      </div>
    </dl>
  );

  const renderMemberActivityBlock = () => (
    <section className="organizationPage__detailBlock organizationPage__detailBlock--activity">
      <div className="organizationPage__detailTitle">
        <strong>최근 활동</strong>
        <span>{selectedMember.recentActivities.length}건</span>
        <button
          className="organizationPage__detailMoreBtn"
          aria-label={"\uB354\uBCF4\uAE30"}
          type="button"
        >
          더보기
        </button>
      </div>
      {selectedMember.recentActivities.length > 0 ? (
        <div className="organizationPage__tableWrap">
          <table className="organizationPage__table organizationPage__table--compact">
            <thead>
              <tr>
                <th>활동</th>
                <th>프로젝트</th>
                <th>일시</th>
              </tr>
            </thead>
            <tbody>
              {selectedMember.recentActivities.slice(0, 10).map((activity) => (
                <tr key={activity.id}>
                  <td>{getMemberActivityTitle(activity)}</td>
                  <td>{activity.projectName || "-"}</td>
                  <td>{formatDateTime(activity.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="organizationPage__treeEmpty">
          최근 활동 기록이 없습니다.
        </p>
      )}
    </section>
  );

  const renderPermissionBlock = () => (
    <section className="organizationPage__detailBlock organizationPage__detailBlock--permission">
      <div className="organizationPage__detailTitle">
        <strong>조직 공통 역할</strong>
        <span>
          {selectedMemberAllowedCount}/{selectedMemberPermissionRows.length}
        </span>
      </div>
      {renderPermissionMemberDetails()}
      <div className="organizationPage__permissionTools">
        <label>
          <span>조직 역할</span>
          <select
            disabled={!canEditSelectedMember}
            onChange={(event) =>
              handleDraftOrganizationRoleChange(event.target.value)
            }
            value={draftOrganizationRole}
          >
            {editableOrganizationRoleOptions.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="organizationPage__permissionActionRow">
        <button
          className="organizationPage__dangerBtn"
          disabled={!canRemoveSelectedMember || removingMember}
          onClick={handleRemoveSelectedMember}
          type="button"
        >
          <UserMinus size={15} aria-hidden="true" />
          <span>{removingMember ? "제외 중" : "조직에서 제외"}</span>
        </button>
        <button
          className="organizationPage__saveBtn"
          disabled={
            !canEditSelectedMember || !selectedMemberHasChanges || savingMember
          }
          onClick={handleSaveSelectedMember}
          type="button"
        >
          <Save size={15} aria-hidden="true" />
          <span>{savingMember ? "저장 중" : "저장"}</span>
        </button>
      </div>
      <div className="organizationPage__permissionSettingGrid">
        {selectedMemberPermissionRows.map((row) => (
          <label
            className={`${row.allowed ? "isAllowed" : ""} ${
              !canEditSelectedPermissions ? "isDisabled" : ""
            }`}
            key={row.key}
            title={row.key}
          >
            <input
              checked={row.allowed}
              disabled={!canEditSelectedPermissions}
              onChange={() => handleToggleSelectedPermission(row.key)}
              type="checkbox"
            />
            <span>{row.label}</span>
          </label>
        ))}
      </div>
    </section>
  );

  const renderReceivedInvitationPanel = () => {
    if (isLoadingInvitations) {
      return (
        <div className="organizationPage__invitePanel">
          {renderOrganizationLoading("조직 초대를 확인하는 중입니다.", true)}
        </div>
      );
    }

    if (receivedInvitations.length === 0) return null;

    return (
      <div className="organizationPage__invitePanel">
        <div className="organizationPage__invitePanelHeader">
          <strong>받은 조직 초대</strong>
          <span>{receivedInvitations.length}건</span>
        </div>
        <div className="organizationPage__inviteList">
          {receivedInvitations.map((invitation) => {
            const invitationId = invitation.id;
            const accepting =
              respondingInvitationId === `accept:${invitationId}`;
            const rejecting =
              respondingInvitationId === `reject:${invitationId}`;
            const isResponding = Boolean(respondingInvitationId);

            return (
              <article
                className="organizationPage__inviteItem"
                key={invitationId}
              >
                <div>
                  <strong>{invitation.organizationName || "조직"}</strong>
                  <span>
                    {invitation.invitedByName
                      ? `${invitation.invitedByName}님이 초대`
                      : "조직 초대"}
                    {" · "}
                    {formatDateTime(invitation.createdAt)}
                  </span>
                </div>
                <div className="organizationPage__inviteActions">
                  <button
                    className="organizationPage__secondaryBtn"
                    disabled={isResponding}
                    onClick={() =>
                      handleRespondInvitation(invitationId, "reject")
                    }
                    type="button"
                  >
                    {rejecting ? "거절 중" : "거절"}
                  </button>
                  <button
                    className="organizationPage__createBtn"
                    disabled={isResponding}
                    onClick={() =>
                      handleRespondInvitation(invitationId, "accept")
                    }
                    type="button"
                  >
                    <UserPlus size={15} aria-hidden="true" />
                    <span>{accepting ? "수락 중" : "수락"}</span>
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    );
  };

  if (!hasAssignedOrganization) {
    return (
      <section className="content organizationPage organizationPage--unassigned">
        <div
          className={`organizationPage__setup ${
            canCreateRootOrganizationNode
              ? ""
              : "organizationPage__setup--inviteOnly"
          }`}
        >
          <section className="organizationPage__setupIntro">
            {canCreateRootOrganizationNode ? (
              <Building2 size={34} aria-hidden="true" />
            ) : (
              <UserPlus size={34} aria-hidden="true" />
            )}
            <h2>
              {canCreateRootOrganizationNode ? "조직 생성" : "조직 초대"}
            </h2>
            <p>
              {canCreateRootOrganizationNode
                ? "소속된 조직이 없습니다. 조직을 만들면 조직 관리자로 지정됩니다."
                : "소속된 조직이 없습니다. 받은 초대가 있으면 수락하여 조직에 참여할 수 있습니다."}
            </p>
          </section>

          {canCreateRootOrganizationNode ? (
            <form
              className="organizationPage__setupForm"
              onSubmit={handleSubmitRootOrganization}
            >
              <label className="organizationPage__formRow">
                <span>조직명</span>
                <input
                  maxLength={100}
                  onChange={(event) =>
                    handleRootOrganizationFormChange("name", event.target.value)
                  }
                  placeholder="조직명을 입력하세요"
                  type="text"
                  value={rootOrganizationForm.name}
                />
              </label>

              <label className="organizationPage__formRow">
                <span>국가</span>
                <select
                  onChange={(event) =>
                    handleRootOrganizationFormChange(
                      "country_code",
                      event.target.value,
                    )
                  }
                  value={rootOrganizationForm.country_code}
                >
                  <option value="">국가 선택</option>
                  {countryOptions.map((country) => {
                    const code = country.code ?? country.country_code ?? "";
                    return (
                      <option key={code} value={code}>
                        {formatCountryLabel(country)}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label className="organizationPage__formRow">
                <span>조직 설명</span>
                <textarea
                  onChange={(event) =>
                    handleRootOrganizationFormChange(
                      "description",
                      event.target.value,
                    )
                  }
                  placeholder="조직 설명을 입력해 주세요."
                  value={rootOrganizationForm.description}
                />
              </label>

              {summaryError && (
                <p className="organizationPage__panelNote isError">
                  {summaryError}
                </p>
              )}

              <button
                className="organizationPage__createBtn"
                disabled={isSubmittingRootOrganization}
                type="submit"
              >
                <Building2 size={15} aria-hidden="true" />
                <span>
                  {isSubmittingRootOrganization ? "생성 중" : "조직 생성"}
                </span>
              </button>
            </form>
          ) : (
            <div className="organizationPage__setupNotice">
              <strong>조직 초대가 필요합니다.</strong>
              <span>
                조직 관리자에게 초대를 요청하거나 받은 초대를 수락해 주세요.
              </span>
            </div>
          )}
          {renderReceivedInvitationPanel()}
        </div>
      </section>
    );
  }

  return (
    <section className="content organizationPage">
      <div className="organizationPage__header">
        <div>
          {canShowOrganizationBreadcrumb ? (
            <h2 className="organizationPage__breadcrumbTitle">
              {organizationBreadcrumbRows.map((organization, index) => {
                const isLast = index === organizationBreadcrumbRows.length - 1;
                return (
                  <span
                    className="organizationPage__breadcrumbSegment"
                    key={`${organization.id}-${index}`}
                  >
                    {index > 0 && (
                      <span
                        aria-hidden="true"
                        className="organizationPage__breadcrumbDivider"
                      >
                        /
                      </span>
                    )}
                    <button
                      aria-current={isLast ? "page" : undefined}
                      className={isLast ? "isCurrent" : ""}
                      onClick={() => handleSelectOrganizationRow(organization)}
                      type="button"
                    >
                      {organization.name}
                    </button>
                  </span>
                );
              })}
            </h2>
          ) : (
            <h2>{selectedOrganization.name}</h2>
          )}
          <p className="organizationPage__membershipContext">
            <span>
              내 소속 : {summaryOrganization?.affiliationName || user?.organization_name || "-"}
            </span>
          </p>
        </div>
      </div>

      <div className="organizationPage__tabs" role="tablist">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              aria-selected={activeTab === tab.id}
              className={activeTab === tab.id ? "isActive" : ""}
              key={tab.id}
              onClick={() => handleOrganizationTabChange(tab.id)}
              role="tab"
              type="button"
            >
              <Icon size={16} aria-hidden="true" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div className="organizationPage__workbench">
        <main className="organizationPage__mainPanel">
          {activeTab === "summary" && (
            <>
              {summaryError && (
                <p className="organizationPage__panelNote isError">
                  {summaryError}
                </p>
              )}
              <div className="organizationPage__grid">
                <OrganizationLatestNotice
                  organizationId={currentOrganizationId}
                />

                <OrganizationSummaryWeekCalendar
                  organizationId={currentOrganizationId}
                />

                <div className="organizationPage__summaryTables">
                <article className="organizationPage__panel">
                  <div className="organizationPage__panelHeader">
                    <h3>인원</h3>
                    <span>{summaryMemberRows.length}명</span>
                  </div>
                  {isLoadingSummary ? (
                    renderOrganizationLoading(
                      "인원 정보를 불러오는 중입니다.",
                      true,
                    )
                  ) : summaryMemberRows.length > 0 ? (
                    <div className="organizationPage__tableWrap">
                      <table className="organizationPage__table organizationPage__table--compact">
                        <thead>
                          <tr>
                            <th>이름</th>
                            <th>소속</th>
                            <th>역할</th>
                            <th>최근 활동</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summaryMemberRows.map((member) => (
                            <tr key={member.id}>
                              <td>
                                <strong className="organizationPage__tableMain">
                                  {member.name}
                                </strong>
                                <span className="organizationPage__tableSub">
                                  {member.email}
                                </span>
                              </td>
                              <td>{member.organizationName || "-"}</td>
                              <td>{member.organizationRole}</td>
                              <td>{formatDateTime(member.latestActivityAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="organizationPage__emptyState isCompact">
                      <UsersRound size={28} aria-hidden="true" />
                      <strong>인원 정보가 없습니다.</strong>
                    </div>
                  )}
                </article>

                <article className="organizationPage__panel">
                  <div className="organizationPage__panelHeader">
                    <h3>프로젝트</h3>
                    <span>{projectCount}개</span>
                  </div>
                  {isLoadingSummary ? (
                    renderOrganizationLoading(
                      "프로젝트를 불러오는 중입니다.",
                      true,
                    )
                  ) : sortedOrganizationProjectRows.length > 0 ? (
                    <div className="organizationPage__tableWrap">
                      <table className="organizationPage__table organizationPage__table--selectable">
                        <thead>
                          <tr>
                            <th>프로젝트</th>
                            <th>생성자</th>
                            <th>생성일</th>
                            <th>최근 변경</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedOrganizationProjectRows
                            .slice(0, 5)
                            .map((project) => (
                              <tr
                                className="organizationPage__clickableRow"
                                key={project.id}
                                onClick={() => handleOpenProject(project)}
                                onKeyDown={(event) =>
                                  handleProjectRowKeyDown(event, project)
                                }
                                role="link"
                                tabIndex={0}
                              >
                                <td>
                                  <span className="organizationPage__linkedCell">
                                    <span>{project.name}</span>
                                    {!isOwnOrganizationProject(project) && (
                                      <ExternalLink size={14} aria-hidden="true" />
                                    )}
                                  </span>
                                </td>
                                <td>{project.createdBy || project.owner}</td>
                                <td>{formatDateTime(project.createdAt)}</td>
                                <td>
                                  {formatDateTime(project.latestChangeAt)}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="organizationPage__emptyState isCompact">
                      <FolderKanban size={28} aria-hidden="true" />
                      <strong>프로젝트가 없습니다.</strong>
                    </div>
                  )}
                </article>
                </div>
              </div>
            </>
          )}

          {activeTab === "notices" && (
            <OrganizationNotices
              notify={notify}
              organizationId={currentOrganizationId}
            />
          )}

          {activeTab === "calendar" && (
            <OrganizationCalendar
              currentUser={user}
              members={summaryMemberRows}
              notify={notify}
              organizationCountryCode={selectedOrganization?.countryCode}
              organizationCountryName={selectedOrganization?.countryName}
              organizationId={currentOrganizationId}
              organizationName={selectedOrganization?.name}
              organizationOptions={organizationTreeRows}
            />
          )}

          {activeTab === "settings" && (
            <>
              <section
                aria-label="조직 설정 메뉴"
                className="organizationPage__settingsNavigation"
              >
                <div>
                  <strong>조직 설정</strong>
                  <span>
                    모든 소속에 공통으로 적용되는 조직 역할을 관리합니다.
                  </span>
                </div>
                <nav role="tablist">
                  {visibleSettingsSections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        aria-selected={settingsSection === section.id}
                        className={
                          settingsSection === section.id ? "isActive" : ""
                        }
                        key={section.id}
                        onClick={() =>
                          handleSettingsSectionChange(section.id)
                        }
                        role="tab"
                        type="button"
                      >
                        <Icon size={15} aria-hidden="true" />
                        <span>{section.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </section>

              {settingsSection === "members" && (
                <div className="organizationPage__memberWorkspace">
                  {renderMemberListPanel(true)}

                  <article className="organizationPage__panel organizationPage__memberDetailPanel">
                    {isLoadingSettingsMembers ? (
                      renderOrganizationLoading(
                        "구성원 상세를 불러오는 중입니다.",
                        true,
                      )
                    ) : selectedMember ? (
                      <div className="organizationPage__memberDetailLayout organizationPage__memberDetailLayout--overview">
                        {renderMemberProfile()}
                        {renderMemberOrganizationMoveBlock()}
                        {renderMemberActivityBlock()}
                      </div>
                    ) : (
                      <div className="organizationPage__emptyState isCompact">
                        <UsersRound size={28} aria-hidden="true" />
                        <strong>구성원을 선택해 주세요.</strong>
                      </div>
                    )}
                  </article>
                </div>
              )}

              {settingsSection === "permissions" &&
                canViewPermissionSettings && (
                  <div className="organizationPage__memberWorkspace">
                    {renderMemberListPanel(true)}

                    <article className="organizationPage__panel organizationPage__memberDetailPanel">
                      {isLoadingSettingsMembers ? (
                        renderOrganizationLoading(
                          "권한 정보를 불러오는 중입니다.",
                          true,
                        )
                      ) : selectedMember ? (
                        <div className="organizationPage__memberDetailLayout">
                          {renderPermissionBlock()}
                        </div>
                      ) : (
                        <div className="organizationPage__emptyState isCompact">
                          <ShieldCheck size={28} aria-hidden="true" />
                          <strong>
                            권한을 설정할 구성원을 선택해 주세요.
                          </strong>
                        </div>
                      )}
                    </article>
                  </div>
                )}

              {settingsSection === "profile" &&
                canUpdateOrganizationSettings && (
                  <article className="organizationPage__panel organizationPage__panel--full">
                    <div className="organizationPage__panelHeader">
                      <h3>기본 정보</h3>
                      <span>{selectedOrganization.name}</span>
                    </div>
                    {isLoadingSummary ? (
                      renderOrganizationLoading(
                        "조직 설정을 불러오는 중입니다.",
                      )
                    ) : (
                      <form
                        className="organizationPage__settingsForm"
                        onSubmit={handleSubmitOrganizationSettings}
                      >
                        <label className="organizationPage__formRow">
                          <span>조직명</span>
                          <input
                            maxLength={100}
                            onChange={(event) =>
                              handleOrganizationSettingsFormChange(
                                "name",
                                event.target.value,
                              )
                            }
                            type="text"
                            value={organizationSettingsForm.name}
                          />
                        </label>
                        <label className="organizationPage__formRow">
                          <span>조직 설명</span>
                          <textarea
                            maxLength={1000}
                            onChange={(event) =>
                              handleOrganizationSettingsFormChange(
                                "description",
                                event.target.value,
                              )
                            }
                            placeholder="조직 설명을 입력해 주세요."
                            value={organizationSettingsForm.description}
                          />
                        </label>
                        <div className="organizationPage__modalActions">
                          <button
                            className="organizationPage__saveBtn"
                            disabled={
                              !organizationSettingsHasChanges ||
                              isSavingOrganizationSettings
                            }
                            type="submit"
                          >
                            <Save size={15} aria-hidden="true" />
                            <span>
                              {isSavingOrganizationSettings
                                ? "저장 중"
                                : "저장"}
                            </span>
                          </button>
                        </div>
                      </form>
                    )}
                  </article>
                )}
            </>
          )}

          {activeTab === "projects" && (
            <article className="organizationPage__panel organizationPage__panel--full">
              <div className="organizationPage__panelHeader organizationPage__panelHeader--withTools">
                <div>
                  <h3>프로젝트</h3>
                  <span>{sortedOrganizationProjectRows.length}개</span>
                </div>
                <label className="organizationPage__sortControl">
                  <span>정렬</span>
                  <select
                    aria-label="조직 프로젝트 정렬"
                    onChange={(event) => setProjectSort(event.target.value)}
                    value={projectSort}
                  >
                    {ORGANIZATION_PROJECT_SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {isLoadingSummary ? (
                renderOrganizationLoading("프로젝트를 불러오는 중입니다.")
              ) : sortedOrganizationProjectRows.length > 0 ? (
                <div className="organizationPage__tableWrap">
                  <table className="organizationPage__table organizationPage__table--selectable">
                    <thead>
                      <tr>
                        <th>프로젝트</th>
                        <th>생성자</th>
                        <th>생성일</th>
                        <th>최근 변경</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedOrganizationProjectRows.map((project) => (
                        <tr
                          className="organizationPage__clickableRow"
                          key={project.id}
                          onClick={() => handleOpenProject(project)}
                          onKeyDown={(event) =>
                            handleProjectRowKeyDown(event, project)
                          }
                          role="link"
                          tabIndex={0}
                        >
                          <td>
                            <span className="organizationPage__linkedCell">
                              <span>{project.name}</span>
                              {!isOwnOrganizationProject(project) && (
                                <ExternalLink size={14} aria-hidden="true" />
                              )}
                            </span>
                          </td>
                          <td>{project.createdBy || project.owner}</td>
                          <td>{formatDateTime(project.createdAt)}</td>
                          <td>{formatDateTime(project.latestChangeAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="organizationPage__emptyState">
                  <FolderKanban size={34} aria-hidden="true" />
                  <strong>프로젝트가 없습니다.</strong>
                </div>
              )}
            </article>
          )}

          {activeTab === "chart" && (
            <article className="organizationPage__panel organizationPage__panel--full organizationPage__chartPanel">
              <div className="organizationPage__panelHeader organizationPage__panelHeader--withTools">
                <div>
                  <h3>전체 조직도</h3>
                  <span>
                    조직 {organizationChart.organizations.length}개 · 구성원{" "}
                    {organizationChart.members.length}명
                  </span>
                </div>
                <p className="organizationPage__chartGuide">
                  최상위 조직부터 모든 소속을 표시합니다.
                </p>
              </div>

              {isLoadingOrganizationChart ? (
                renderOrganizationLoading("조직도를 불러오는 중입니다.")
              ) : organizationChartError ? (
                <div className="organizationPage__emptyState">
                  <GitBranch size={34} aria-hidden="true" />
                  <strong>{organizationChartError}</strong>
                  <button
                    className="organizationPage__secondaryBtn"
                    onClick={loadOrganizationChart}
                    type="button"
                  >
                    다시 불러오기
                  </button>
                </div>
              ) : organizationChartForest.length > 0 ? (
                <div className="organizationPage__chartViewport">
                  <ul className="organizationPage__chartRoot">
                    {organizationChartForest.map((rootNode) => (
                      <OrganizationChartNode
                        canCreateChild={canCreateChildOrganizationAt}
                        canDelete={canDeleteChildOrganizationAt}
                        canManageMember={canManageChartMember}
                        currentOrganizationId={
                          organizationChart.currentOrganizationId
                        }
                        key={rootNode.id}
                        node={rootNode}
                        onCreateChild={handleOpenCreateOrganization}
                        onDelete={handleOpenDeleteOrganization}
                        onManageMember={handleManageChartMember}
                        onSelectOrganization={handleSelectOrganizationRow}
                      />
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="organizationPage__emptyState">
                  <GitBranch size={34} aria-hidden="true" />
                  <strong>표시할 조직이 없습니다.</strong>
                </div>
              )}
            </article>
          )}

        </main>
      </div>

      {isRemoveMemberConfirmOpen && selectedMember && (
        <div
          className="organizationPage__modalBackdrop"
          onMouseDown={handleCloseRemoveMemberConfirm}
          role="presentation"
        >
          <section
            aria-describedby="organization-remove-member-description"
            aria-labelledby="organization-remove-member-title"
            aria-modal="true"
            className="organizationPage__modal organizationPage__modal--confirm"
            onMouseDown={(event) => event.stopPropagation()}
            role="alertdialog"
          >
            <div className="organizationPage__modalHeader">
              <div>
                <h3 id="organization-remove-member-title">조직에서 제외</h3>
                <span>확인 필요</span>
              </div>
              <button
                aria-label="닫기"
                className="organizationPage__modalClose"
                disabled={removingMember}
                onClick={handleCloseRemoveMemberConfirm}
                type="button"
              >
                <X size={17} aria-hidden="true" />
              </button>
            </div>

            <div
              className="organizationPage__modalBody"
              id="organization-remove-member-description"
            >
              <div className="organizationPage__confirmIntro">
                <span className="organizationPage__confirmIcon">
                  <UserMinus size={20} aria-hidden="true" />
                </span>
                <div className="organizationPage__confirmText">
                  <strong>
                    이 사용자를 {selectedMemberOrganization?.name ||
                      selectedOrganization?.name ||
                      "선택한 조직"}에서 제외하시겠습니까?
                  </strong>
                  <p>제외 후 해당 조직의 사용자 목록과 권한에서 제거됩니다.</p>
                </div>
              </div>

              <dl className="organizationPage__confirmMeta">
                <div>
                  <dt>이름</dt>
                  <dd>{selectedMember.name || "-"}</dd>
                </div>
                <div>
                  <dt>이메일</dt>
                  <dd>{selectedMember.email || "-"}</dd>
                </div>
                <div>
                  <dt>조직</dt>
                  <dd>
                    {selectedMemberOrganization?.name ||
                      selectedOrganization?.name ||
                      "-"}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="organizationPage__modalActions">
              <button
                autoFocus
                className="organizationPage__secondaryBtn"
                disabled={removingMember}
                onClick={handleCloseRemoveMemberConfirm}
                type="button"
              >
                취소
              </button>
              <button
                className="organizationPage__dangerBtn"
                disabled={removingMember}
                onClick={handleConfirmRemoveSelectedMember}
                type="button"
              >
                <UserMinus size={15} aria-hidden="true" />
                <span>{removingMember ? "제외 중" : "제외"}</span>
              </button>
            </div>
          </section>
        </div>
      )}

      {isCreateUserOpen && (
        <div
          className="organizationPage__modalBackdrop"
          onMouseDown={handleCloseCreateUser}
          role="presentation"
        >
          <form
            className="organizationPage__modal"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={handleSubmitCreateUser}
            role="dialog"
            aria-modal="true"
            aria-labelledby="organization-create-user-title"
          >
            <div className="organizationPage__modalHeader">
              <div>
                <h3 id="organization-create-user-title">조직 초대</h3>
                <span>{selectedOrganization.name}에 구성원을 초대합니다</span>
              </div>
              <button
                aria-label="닫기"
                className="organizationPage__modalClose"
                onClick={handleCloseCreateUser}
                type="button"
              >
                <X size={17} aria-hidden="true" />
              </button>
            </div>

            <div className="organizationPage__modalBody">
              <label className="organizationPage__formRow">
                <span>이메일</span>
                <input
                  autoFocus
                  onChange={(event) =>
                    handleCreateUserFormChange("email", event.target.value)
                  }
                  placeholder="초대할 사용자의 이메일"
                  type="email"
                  value={createUserForm.email}
                />
              </label>
            </div>

            <div className="organizationPage__modalActions">
              <button
                className="organizationPage__secondaryBtn"
                onClick={handleCloseCreateUser}
                type="button"
              >
                취소
              </button>
              <button
                className="organizationPage__createBtn"
                disabled={isSubmittingCreateUser}
                type="submit"
              >
                <UserPlus size={15} aria-hidden="true" />
                <span>{isSubmittingCreateUser ? "초대 중" : "초대 보내기"}</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {deleteOrganizationTarget && (
        <div
          className="organizationPage__modalBackdrop"
          onMouseDown={handleCloseDeleteOrganization}
          role="presentation"
        >
          <section
            aria-describedby="organization-delete-affiliation-description"
            aria-labelledby="organization-delete-affiliation-title"
            aria-modal="true"
            className="organizationPage__modal organizationPage__modal--confirm"
            onMouseDown={(event) => event.stopPropagation()}
            role="alertdialog"
          >
            <div className="organizationPage__modalHeader">
              <div>
                <h3 id="organization-delete-affiliation-title">소속 삭제</h3>
                <span>확인 필요</span>
              </div>
              <button
                aria-label="닫기"
                className="organizationPage__modalClose"
                disabled={isDeletingOrganization}
                onClick={handleCloseDeleteOrganization}
                type="button"
              >
                <X size={17} aria-hidden="true" />
              </button>
            </div>

            <div
              className="organizationPage__modalBody"
              id="organization-delete-affiliation-description"
            >
              <div className="organizationPage__confirmIntro">
                <span className="organizationPage__confirmIcon">
                  <Trash2 size={20} aria-hidden="true" />
                </span>
                <div className="organizationPage__confirmText">
                  <strong>
                    {deleteOrganizationTarget.name || "선택한 소속"}을
                    삭제하시겠습니까?
                  </strong>
                  <p>
                    구성원과 일정·프로젝트 등 소속 데이터는 최상위 조직으로
                    이전됩니다. 내부 소속이 남아 있다면 삭제할 수 없습니다.
                  </p>
                </div>
              </div>

              <dl className="organizationPage__confirmMeta">
                <div>
                  <dt>삭제 소속</dt>
                  <dd>{deleteOrganizationTarget.name || "-"}</dd>
                </div>
                <div>
                  <dt>직속 구성원</dt>
                  <dd>{deleteOrganizationTarget.members?.length ?? 0}명</dd>
                </div>
                <div>
                  <dt>데이터 이전 위치</dt>
                  <dd>{rootRoleOrganization?.name || "Root Organization"}</dd>
                </div>
              </dl>
            </div>

            <div className="organizationPage__modalActions">
              <button
                autoFocus
                className="organizationPage__secondaryBtn"
                disabled={isDeletingOrganization}
                onClick={handleCloseDeleteOrganization}
                type="button"
              >
                취소
              </button>
              <button
                className="organizationPage__dangerBtn"
                disabled={isDeletingOrganization}
                onClick={handleConfirmDeleteOrganization}
                type="button"
              >
                <Trash2 size={15} aria-hidden="true" />
                <span>{isDeletingOrganization ? "삭제 중" : "삭제"}</span>
              </button>
            </div>
          </section>
        </div>
      )}

      {isCreateOrganizationOpen && (
        <div
          className="organizationPage__modalBackdrop"
          onMouseDown={handleCloseCreateOrganization}
          role="presentation"
        >
          <form
            className="organizationPage__modal"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={handleSubmitCreateOrganization}
            role="dialog"
            aria-modal="true"
            aria-labelledby="organization-create-child-title"
          >
            <div className="organizationPage__modalHeader">
              <div>
                <h3 id="organization-create-child-title">소속 생성</h3>
                <span>즉시 생성</span>
              </div>
              <button
                aria-label="닫기"
                className="organizationPage__modalClose"
                onClick={handleCloseCreateOrganization}
                type="button"
              >
                <X size={17} aria-hidden="true" />
              </button>
            </div>

            <div className="organizationPage__modalBody">
              <div className="organizationPage__createOrganizationParent">
                <Building2 size={16} aria-hidden="true" />
                <span>
                  <small>
                    {getOrganizationActualId(createOrganizationParent) ===
                    normalizeComparableId(organizationChart.rootOrganizationId)
                      ? "Root Organization"
                      : "상위 소속"}
                  </small>
                  <strong>{createOrganizationParent?.name || "-"}</strong>
                </span>
              </div>
              <label className="organizationPage__formRow">
                <span>소속명</span>
                <input
                  autoFocus
                  maxLength={100}
                  onChange={(event) =>
                    handleCreateOrganizationFormChange(
                      "name",
                      event.target.value,
                    )
                  }
                  type="text"
                  value={createOrganizationForm.name}
                />
              </label>
              <label className="organizationPage__formRow">
                <span>국가</span>
                <select
                  onChange={(event) =>
                    handleCreateOrganizationFormChange(
                      "country_code",
                      event.target.value,
                    )
                  }
                  value={createOrganizationForm.country_code}
                >
                  <option value="">국가 선택</option>
                  {countryOptions.map((country) => {
                    const code = country.code ?? country.country_code ?? "";
                    return (
                      <option key={code} value={code}>
                        {formatCountryLabel(country)}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="organizationPage__formRow">
                <span>조직 설명</span>
                <textarea
                  onChange={(event) =>
                    handleCreateOrganizationFormChange(
                      "description",
                      event.target.value,
                    )
                  }
                  placeholder="소속에 대한 설명을 입력해주세요."
                  value={createOrganizationForm.description}
                />
              </label>
            </div>

            <div className="organizationPage__modalActions">
              <button
                className="organizationPage__secondaryBtn"
                onClick={handleCloseCreateOrganization}
                type="button"
              >
                취소
              </button>
              <button
                className="organizationPage__createBtn"
                disabled={isSubmittingCreateOrganization}
                type="submit"
              >
                <Building2 size={15} aria-hidden="true" />
                <span>
                  {isSubmittingCreateOrganization ? "생성 중" : "생성"}
                </span>
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
